// CONTRACTING — the signable-document instrument (tab-contracting).
//
// The study's finding, in one line: the old page was five filing cabinets behind five tabs, and
// the operator's actual question — *is this fit to sign, and if not what is stopping it* — was
// answered nowhere. Five tabs became five ZONES on one sheet-stack, in the order the work runs:
//
//   masthead · rail · THE VERDICT
//   1  Fit to sign      — R1…R8, each row stating its own evidence and routing to the fix
//   2  The document     — pipeline, variables, clauses, section map, the canonical preview
//   3  Payment schedule — coverage named, one explicit editor per row
//   4  Change requests  — hidden until there is a baseline or a request, composer shows the wording
//      The record       — baselines, the diff, the signature affirmation, the clause library
//
// THREE LAWS THIS FILE IS WRITTEN AGAINST, and a reviewer should fail a PR on any of them:
//
//  1. `.btn-accent` appears EXACTLY ONCE in the page body, in the verdict line. Everything else
//     — New version, Save & re-render, Add milestone, Compare, Revise — is bordered or ghost.
//     A page with six primaries has none. (Modal footers keep their own commit: a modal is its
//     own surface and its confirm is the only thing it is for.)
//  2. Every readiness row renders a string the SERVER composed — the linter's message, the gate
//     engine's `detail`, the generator's literal `None recorded.` — never a paraphrase. The
//     paraphrase is where drift lives (B7).
//  3. The pre-flight and the issue dialog read the SAME lint walk over the SAME context
//     (`lintFailuresOf` in services/sowRender.ts). The page must never promise a document is fit
//     while POST /issue refuses it.
//
// Nothing the old page could do was removed: every version, milestone, change-request, baseline
// and clause verb is still here, re-housed in the zone that owns it.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle, Calculator, Check, ChevronRight, ExternalLink, FileDown, FileSignature,
  GitCompareArrows, Pencil, PenLine, Plus, Save, Send, ShieldCheck, Trash2, Undo2, X,
} from 'lucide-react';

import { useLocale, useT } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './ContractingPage.de.js';
import './ContractingPage.fr.js';
import { useEngagement } from '../context/EngagementContext.js';
import { Tx, TxTools } from '../components/Tx.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll } from '../hooks/usePoll.js';
import { ROUTES, scrollToAnchor, useDeepLink, useOpenRoute, useRouteParams } from '../routes.js';
import { cutRun, type RailCell, type RailTone } from '../../shared/rail.js';
import { REQUIREMENT_STATUS_LABEL } from '../../shared/requirementStatus.js';
import { registerApi, type RegisterRow } from '../api/register.js';
import {
  contractingApi,
  type BaselineDiff, type BaselineRow, type ChangeRequest, type ChangeRequestDetail,
  type ClauseEntry, type ClauseUsage, type ContractingMeta, type ContractingReadiness,
  type CrStatus, type DeletionPlan, type DiffGroup, type IssueCheck, type Milestone,
  type ReadinessCandidate, type RevisionInstruction, type Sow, type SowVersionDetail,
  type SowVersionRow,
} from '../api/contracting.js';

/** The page's own cadence. Slower than a live feed; a contract does not move every second. */
const POLL_MS = 20_000;

// ────────────────────────────────────────────────────────────────────────────
// The rail
// ────────────────────────────────────────────────────────────────────────────

type CtrRailKey = 'DOCUMENT' | 'WOULD PRINT' | 'VALUE' | 'NOT FIT TO SIGN' | 'G3 · SOW SIGNED';
const RAIL_KEYS: readonly CtrRailKey[] = ['DOCUMENT', 'WOULD PRINT', 'VALUE', 'NOT FIT TO SIGN', 'G3 · SOW SIGNED'];

/** The rail's own narrowing of the shared cell (BL-5): same shape, this tab's key union. */
interface CtrRailCell extends RailCell { key: CtrRailKey }

const TONE_CLASS: Partial<Record<RailTone, string>> = {
  warn: ' fv--warn',
  attend: ' fv--warn',
  ok: ' fv--ok',
  err: ' fv--err',
  broken: ' fv--err',
  nil: ' fv--nil',
};

// ────────────────────────────────────────────────────────────────────────────
// The readiness ladder
// ────────────────────────────────────────────────────────────────────────────

type RuleKey = 'acs' | 'excl' | 'narr' | 'ms' | 'vars' | 'value' | 'reg';

/**
 * One readiness row, in the shape every tab ladder emits (UNIFIED-FUNCTION F3): a rule name, a
 * weight, a title, a consequence sentence, a route. Declared locally rather than imported from
 * `src/server/utils/queueRules.ts` — no file under src/client imports from src/server, and
 * `queueRules` would drag `pg` into the browser bundle (DocumentsPage states the same rule).
 *
 * The ORDER is the contract and it never depends on the values: severity first, declaration
 * order within. A ladder that re-ranks itself as the data moves cannot be learned.
 */
interface ReadyRow {
  rule: RuleKey;
  weight: number;
  /** `broken` = the server refuses. `attend` = worth fixing, nothing stops you. `ok` = met. */
  state: 'broken' | 'attend' | 'ok';
  title: ReactNode;
  why: ReactNode;
  body: ReactNode;
  /** The destination LABEL. A property of the rule, never of the values (the fix column). */
  routeLabel: string | null;
  fix: { do: ReactNode; control?: ReactNode } | null;
  /** What the rail says about this rule when it is the worst one outstanding. */
  short: string;
}

const RULE_WEIGHT: Record<RuleKey, number> = {
  acs: 10, excl: 20, narr: 30, ms: 40, vars: 50, value: 60, reg: 70,
};

// ────────────────────────────────────────────────────────────────────────────
// Small shared bits
// ────────────────────────────────────────────────────────────────────────────

const short = (hash: string | null | undefined): string => (hash ? String(hash).slice(0, 12) : '—');
const short10 = (hash: string | null | undefined): string => (hash ? String(hash).slice(0, 10) : '—');

/**
 * The register is the source; this is the one number the operator sanity-checks before issuing.
 * Same shape the SOW prints (services/sowRender.ts formatMoney) so the screen and the contract
 * never disagree about what CHF 24'000 looks like.
 */
function money(amount: string | number | null | undefined, currency = 'CHF'): string {
  const n = Number(amount);
  if (amount === null || amount === undefined || amount === '' || !Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString('en-CH', { maximumFractionDigits: 2 })}`;
  }
}

function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const cls =
    status === 'signed' || status === 'approved' || status === 'accepted' ? 'badge-ok'
      : status === 'issued' || status === 'submitted' || status === 'in_progress' ? 'badge-accent'
        : status === 'rejected' || status === 'withdrawn' ? 'badge-err'
          : status === 'deferred' || status === 'superseded' ? 'badge-warn'
            : 'badge-plain';
  return <span className={`badge ${cls}`}>{t(status.replace(/_/g, ' '))}</span>;
}

/** LAW 6 — a SOW version or a CR that is still proposed carries ◇; committed carries ▪. */
function ClaimMark({ status }: { status: string }) {
  if (status === 'signed' || status === 'approved') return <span className="att att--held" aria-hidden="true" />;
  if (status === 'rejected' || status === 'withdrawn' || status === 'superseded') return null;
  return <span className="att att--proposed" aria-hidden="true" />;
}

/**
 * The one modal for a refused delete. The server's plan carries the blocker's own message, and
 * every message names the alternative — so the operator reads what to do instead of staring at a
 * dead button. There is no Delete button here at all: it is not disabled, it is not offered.
 */
function BlockedDeleteModal({ title, plan, onClose }: { title: string; plan: DeletionPlan; onClose: () => void }) {
  const t = useT();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-ink-3 mb-12">{plan.label}</p>
          {plan.blockers.map((b) => (
            <div key={b.code} className="card p-12 mb-8" style={{ borderLeft: '2px solid var(--err)' }}>
              <div className="flex items-start gap-8">
                <AlertTriangle className="icon-sm" />
                <div className="text-sm text-ink-2">{b.message}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-accent" onClick={onClose}>{t('Understood')}</button>
        </div>
      </div>
    </div>
  );
}

/** The cascade in plain words, for the confirm dialog of a delete the server has allowed. */
function CascadeList({ plan }: { plan: DeletionPlan }) {
  const t = useT();
  if (!plan.cascade.length) {
    return <p className="text-sm text-ink-2">{t('Nothing else points at it — the row goes on its own.')}</p>;
  }
  return (
    <>
      <p className="text-sm text-ink-2">{t('This also removes:')}</p>
      <ul className="text-sm text-ink-2 mt-4" style={{ paddingLeft: 18, listStyle: 'disc' }}>
        {plan.cascade.map((c) => (
          <li key={c.what}><span className="voice-figure">{c.count}</span> {c.what}</li>
        ))}
      </ul>
    </>
  );
}

/** Everything one poll of this page reads. One fetch cycle, five sources, no partial blanking. */
interface Bundle {
  /**
   * WHOSE bundle this is. `usePoll` keeps the last good value across a re-fetch — which is the
   * whole point of it — so on an engagement switch the previous engagement's rows are still in
   * hand for one beat. Without this stamp the page would draw AVANTI01's masthead over ZZSB1's
   * document, which is the one lie a contracting page must never tell.
   */
  engagementId: number | null;
  sows: Sow[];
  milestones: Milestone[];
  plannedValue: number;
  currency: string;
  crs: ChangeRequest[];
  crTotals: Record<string, number>;
  baselines: BaselineRow[];
  readiness: ContractingReadiness | null;
  /** Readiness is the only source whose failure the rest of the page survives without it. */
  readinessError: string | null;
}

/** The legacy `?tab=` intent, kept working: the tabs became zones, the deep links did not move. */
const TAB_ANCHOR: Record<string, string> = {
  sow: 'z-doc',
  schedule: 'z-sched',
  crs: 'z-cr',
  baselines: 'z-record',
  clauses: 'z-record',
  ready: 'z-ready',
};

const MILESTONE_STATUSES = ['planned', 'in_progress', 'delivered', 'accepted', 'invoiced'] as const;

// ════════════════════════════════════════════════════════════════════════════

export default function ContractingPage() {
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const routeParams = useRouteParams();
  const toast = useToast();
  const { t, tp, fmtDate, fmtDateTime } = useLocale();
  const engagementId = current?.id ?? null;

  const err = useCallback((e: unknown, fallback: string) => toast('err', e instanceof Error ? e.message : fallback), [toast]);

  // ── page-local state ──────────────────────────────────────────────────────
  const [meta, setMeta] = useState<ContractingMeta | null>(null);
  const [clauses, setClauses] = useState<ClauseEntry[]>([]);

  const [versionId, setVersionId] = useState<number | null>(null);
  const [version, setVersion] = useState<SowVersionDetail | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});
  const [clauseKeys, setClauseKeys] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  /** Candidate values accepted BEFORE a document exists. They ride the create call. */
  const [staged, setStaged] = useState<Record<string, string>>({});

  const [openRules, setOpenRules] = useState<Set<RuleKey>>(() => new Set<RuleKey>(['acs']));
  const [recordOpen, setRecordOpen] = useState(false);

  const [issueTarget, setIssueTarget] = useState<SowVersionDetail | null>(null);
  const [issueCheck, setIssueCheck] = useState<IssueCheck | null>(null);
  const [acceptUnverifiable, setAcceptUnverifiable] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState('');
  const [signDate, setSignDate] = useState('');
  const [signAffirmed, setSignAffirmed] = useState(false);
  const [newSowOpen, setNewSowOpen] = useState(false);
  const [newSowTitle, setNewSowTitle] = useState('');

  const [msDraft, setMsDraft] = useState<{ name: string; due_date: string; amount: string }>({ name: '', due_date: '', amount: '' });
  const [msAddOpen, setMsAddOpen] = useState(false);
  const [msEdit, setMsEdit] = useState<
    { id: number; name: string; due_date: string; amount: string; status: string; published: boolean } | null
  >(null);

  type DeleteKind = 'milestone' | 'change_request' | 'sow_version';
  const [del, setDel] = useState<{ kind: DeleteKind; id: number; name: string; plan: DeletionPlan } | null>(null);
  const [delBusy, setDelBusy] = useState(false);

  const [impactEdit, setImpactEdit] = useState<{ impact_cost: string; impact_days: string; impact_risk: string; goodwill: boolean } | null>(null);
  const [submitTarget, setSubmitTarget] = useState<ChangeRequestDetail | null>(null);
  const [crDetail, setCrDetail] = useState<ChangeRequestDetail | null>(null);
  const [crFormOpen, setCrFormOpen] = useState(false);
  const [crForm, setCrForm] = useState<Record<string, string | boolean>>({
    title: '', description: '', origin: '', impact_cost: '', impact_days: '', impact_risk: '', goodwill: false,
  });
  const [approveOpen, setApproveOpen] = useState(false);
  const [instructions, setInstructions] = useState<RevisionInstruction[]>([]);
  const [approveGoodwill, setApproveGoodwill] = useState(false);
  const [approveAddendum, setApproveAddendum] = useState(false);
  const [registerRows, setRegisterRows] = useState<RegisterRow[]>([]);

  const [diffFrom, setDiffFrom] = useState<string>('');
  const [diffTo, setDiffTo] = useState<string>('live');
  const [diff, setDiff] = useState<BaselineDiff | null>(null);

  const [clauseKey, setClauseKey] = useState<string | null>(null);
  const [clauseDraft, setClauseDraft] = useState<{ key: string; title: string; body_md: string }>({ key: '', title: '', body_md: '' });
  const [clauseUsage, setClauseUsage] = useState<ClauseUsage | null>(null);
  const [clauseNew, setClauseNew] = useState(false);

  // ── the one poll (F5.3) ───────────────────────────────────────────────────
  // Five sources, one cycle. Readiness is caught rather than thrown: the schedule and the change
  // requests are still true when the pre-flight cannot be computed, and blanking them would be a
  // lie about the world rather than about the fetch.
  const load = useCallback(async (): Promise<Bundle> => {
    if (!engagementId) {
      return {
        engagementId: null,
        sows: [], milestones: [], plannedValue: 0, currency: 'CHF', crs: [], crTotals: {},
        baselines: [], readiness: null, readinessError: null,
      };
    }
    const [sowsRes, msRes, crRes, blRes, rdRes] = await Promise.all([
      contractingApi.sows(engagementId),
      contractingApi.milestones(engagementId),
      contractingApi.changeRequests(engagementId),
      contractingApi.baselines(engagementId),
      contractingApi.readiness(engagementId).then(
        (r) => ({ ok: true as const, r }),
        (e: unknown) => ({ ok: false as const, message: e instanceof Error ? e.message : t('The pre-flight did not answer.') }),
      ),
    ]);
    return {
      engagementId,
      sows: sowsRes.rows,
      milestones: msRes.rows,
      plannedValue: msRes.planned_value,
      currency: msRes.currency || crRes.currency || 'CHF',
      crs: crRes.rows,
      crTotals: crRes.totals,
      baselines: blRes.rows,
      readiness: rdRes.ok ? rdRes.r : null,
      readinessError: rdRes.ok ? null : rdRes.message,
    };
  }, [engagementId, t]);

  const poll = usePoll<Bundle>(load, POLL_MS);
  const refresh = poll.refresh;

  // Data from the PREVIOUS engagement is not stale, it is wrong — it is drawn from a different
  // subject. It is dropped, not aged.
  const data = poll.data && poll.data.engagementId === engagementId ? poll.data : null;
  const sows = data?.sows ?? [];
  const milestones = data?.milestones ?? [];
  const plannedValue = data?.plannedValue ?? 0;
  const currency = data?.currency ?? 'CHF';
  const crs = data?.crs ?? [];
  const crTotals = data?.crTotals ?? {};
  const baselines = data?.baselines ?? [];
  const readiness = data?.readiness ?? null;
  const readinessError = data?.readinessError ?? null;

  const loadMeta = useCallback(async () => {
    try { setMeta(await contractingApi.meta()); } catch (e) { err(e, t('Failed to load contracting metadata')); }
  }, [err, t]);

  const loadClauses = useCallback(async () => {
    try { setClauses((await contractingApi.clauses()).rows); } catch (e) { err(e, t('Failed to load the clause library')); }
  }, [err, t]);

  const loadVersion = useCallback(async (id: number, forEngagement: number | null) => {
    try {
      const v = await contractingApi.version(id);
      // Belt and braces: a version fetch that lands after the operator has moved on belongs to
      // an engagement this page is no longer showing.
      if (forEngagement !== null && v.engagement_id !== forEngagement) return;
      setVersion(v);
      setVars({ ...(v.variable_values as Record<string, string>) });
      setClauseKeys((v.clause_versions ?? []).map((c) => c.key));
      setOverrides({ ...(v.section_overrides ?? {}) });
      setDirty(false);
    } catch (e) { err(e, t('Failed to load the SOW version')); }
  }, [err, t]);

  useEffect(() => { void loadMeta(); void loadClauses(); }, [loadMeta, loadClauses]);

  // A change of engagement resets everything that is scoped to one.
  useEffect(() => {
    setVersionId(null); setVersion(null); setDiff(null); setCrDetail(null);
    setDiffFrom(''); setStaged({}); setMsEdit(null);
  }, [engagementId]);

  const allVersions: SowVersionRow[] = useMemo(() => sows.flatMap((s) => s.versions), [sows]);

  useEffect(() => {
    if (versionId === null && allVersions.length) setVersionId(allVersions[0].id);
  }, [versionId, allVersions]);

  useEffect(() => { if (versionId) void loadVersion(versionId, engagementId); }, [versionId, engagementId, loadVersion]);

  useEffect(() => {
    if (!diffFrom && baselines.length) setDiffFrom(String(baselines[0].id));
  }, [diffFrom, baselines]);

  // ── arrival intents (F1 receiver) ─────────────────────────────────────────
  useDeepLink(routeParams, {
    tab: (v) => {
      if (v === 'baselines' || v === 'clauses') setRecordOpen(true);
      scrollToAnchor(TAB_ANCHOR[v] ?? 'z-ready');
    },
    rule: (v) => {
      setOpenRules((prev) => new Set([...prev, v as RuleKey]));
      scrollToAnchor('z-ready');
    },
  });

  // ── SOW actions ───────────────────────────────────────────────────────────
  const isDraft = version?.status === 'draft';
  const headVersion: SowVersionRow | null = useMemo(
    () => allVersions.find((v) => v.status !== 'superseded') ?? allVersions[0] ?? null,
    [allVersions],
  );

  const setVar = (key: string, value: string) => { setVars((p) => ({ ...p, [key]: value })); setDirty(true); };
  const toggleClause = (key: string) => {
    setClauseKeys((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
    setDirty(true);
  };
  const setOverride = (key: string, value: string) => { setOverrides((p) => ({ ...p, [key]: value })); setDirty(true); };

  const saveDraft = async (extra: { repin_clauses?: boolean } = {}) => {
    if (!version) return;
    setBusy(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v && v.trim()));
      const out = await contractingApi.updateVersion(version.id, {
        variable_values: vars, clause_keys: clauseKeys, section_overrides: cleaned, ...extra,
      });
      setVersion(out);
      setOverrides({ ...(out.section_overrides ?? {}) });
      setClauseKeys((out.clause_versions ?? []).map((c) => c.key));
      setVars({ ...(out.variable_values as Record<string, string>) });
      setDirty(false);
      toast('ok', t('Re-rendered from the register · {hash}', { hash: short(out.content_hash) }));
      await refresh();
    } catch (e) { err(e, t('Could not save the draft')); } finally { setBusy(false); }
  };

  const newVersion = async (sowId: number, variableValues?: Record<string, string>) => {
    setBusy(true);
    try {
      const v = await contractingApi.createVersion(sowId, variableValues && Object.keys(variableValues).length
        ? { variable_values: variableValues } : {});
      toast('ok', t('Version {n} rendered from the live register', { n: v.version_no }));
      setStaged({});
      await refresh();
      setVersionId(v.id);
      scrollToAnchor('z-doc');
    } catch (e) { err(e, t('Could not create the version')); } finally { setBusy(false); }
  };

  const createSow = async () => {
    if (!engagementId) return;
    try {
      const s = await contractingApi.createSow(engagementId, newSowTitle || undefined);
      setNewSowOpen(false); setNewSowTitle('');
      await newVersion(s.id, staged);
    } catch (e) { err(e, t('Could not create the SOW')); }
  };

  /** Open the issue dialog on the FACTS: the hash the client will sign, and what is wrong. */
  const openIssue = async (v: SowVersionDetail) => {
    setIssueTarget(v);
    setIssueCheck(null);
    setAcceptUnverifiable(false);
    try { setIssueCheck(await contractingApi.issueCheck(v.id)); }
    catch (e) { err(e, t('Could not check the version before issuing')); setIssueTarget(null); }
  };

  const doIssue = async () => {
    if (!issueTarget) return;
    setBusy(true);
    try {
      const out = await contractingApi.issueVersion(issueTarget.id, {
        confirm_unverifiable_acs: acceptUnverifiable || undefined,
      });
      toast('ok', out.notice ?? t('Issued.'));
      if (out.register_snapshot_changed) toast('warn', t('The register had moved — the issued document reflects today.'));
      setIssueTarget(null); setIssueCheck(null); setAcceptUnverifiable(false);
      await refresh(); await loadVersion(out.id, engagementId);
    } catch (e) { err(e, t('Could not issue the version')); } finally { setBusy(false); }
  };

  const doSign = async () => {
    if (!version) return;
    setBusy(true);
    try {
      const out = await contractingApi.markSigned(version.id, {
        typed_name: signName, affirmation: signAffirmed, signed_on: signDate || undefined,
      });
      toast('ok', out.notice ?? t('Signature recorded.'));
      setSignOpen(false); setSignName(''); setSignAffirmed(false); setSignDate('');
      await refresh(); await loadVersion(out.id, engagementId);
    } catch (e) { err(e, t('Could not record the signature')); } finally { setBusy(false); }
  };

  /**
   * Accept one candidate. It is a COPY, never an auto-fill: the value is written into
   * `variable_values` only when the operator says so, and the column it came from was printed
   * beside it at the moment they did. Before a document exists the value is staged and rides the
   * create call — the same commit, one step earlier.
   */
  const useCandidate = async (c: ReadinessCandidate) => {
    if (!version || version.status !== 'draft') {
      setStaged((p) => ({ ...p, [c.key]: c.value }));
      toast('ok', t('{key} staged — it goes in when the document is created', { key: c.key }));
      return;
    }
    setBusy(true);
    try {
      const out = await contractingApi.updateVersion(version.id, {
        variable_values: { ...(version.variable_values as Record<string, string>), ...vars, [c.key]: c.value },
      });
      setVersion(out);
      setVars({ ...(out.variable_values as Record<string, string>) });
      setDirty(false);
      toast('ok', t('{key} written · re-rendered {hash}', { key: c.key, hash: short(out.content_hash) }));
      await refresh();
    } catch (e) { err(e, t('Could not write the value')); } finally { setBusy(false); }
  };

  // ── delete (one flow for every kind on this page) ──────────────────────────
  const deletionPlanFor = useCallback((kind: DeleteKind, id: number): Promise<DeletionPlan> => (
    kind === 'milestone' ? contractingApi.milestoneDeletionPlan(id)
      : kind === 'change_request' ? contractingApi.changeRequestDeletionPlan(id)
        : contractingApi.versionDeletionPlan(id)
  ), []);

  /** Ask the server first: what goes with this, and may it go at all. Then open the dialog. */
  const askDelete = async (kind: DeleteKind, id: number, name: string) => {
    try {
      setDel({ kind, id, name, plan: await deletionPlanFor(kind, id) });
    } catch (e) { err(e, t('Could not work out what deleting this would do')); }
  };

  const doDelete = async () => {
    if (!del) return;
    setDelBusy(true);
    try {
      if (del.kind === 'milestone') await contractingApi.deleteMilestone(del.id);
      else if (del.kind === 'change_request') await contractingApi.deleteChangeRequest(del.id);
      else await contractingApi.deleteVersion(del.id);

      toast('ok', t('{name} deleted', { name: del.name }));
      if (del.kind === 'change_request' && crDetail?.id === del.id) setCrDetail(null);
      if (del.kind === 'sow_version') { setVersion(null); setVersionId(null); }
      setDel(null);
      await refresh();
    } catch (e) {
      // The server recomputes the plan on the DELETE — it never trusts the copy the dialog was
      // drawn from. If it refuses now, that is news: re-read the plan and show the refusal with
      // the alternative it names, rather than a toast the operator has to interpret.
      err(e, t('Could not delete'));
      try { setDel({ ...del, plan: await deletionPlanFor(del.kind, del.id) }); } catch { setDel(null); }
    } finally { setDelBusy(false); }
  };

  // ── milestones ────────────────────────────────────────────────────────────
  const addMilestone = async () => {
    if (!engagementId || !msDraft.name.trim()) return;
    try {
      await contractingApi.createMilestone({
        engagement_id: engagementId, name: msDraft.name,
        due_date: msDraft.due_date || null, amount: msDraft.amount || null,
      });
      setMsDraft({ name: '', due_date: '', amount: '' });
      setMsAddOpen(false);
      await refresh();
      toast('ok', t('Milestone added'));
    } catch (e) { err(e, t('Could not add the milestone')); }
  };

  /**
   * ONE explicit write per row. The old grid PATCHed on blur from five live inputs — including
   * the status select and the portal-visibility toggle, which publishes to the client — with no
   * `·writes` anywhere and no way to change your mind. Editing is now a mode you enter and a
   * Save you press.
   */
  const saveMilestone = async () => {
    if (!msEdit) return;
    setBusy(true);
    try {
      await contractingApi.updateMilestone(msEdit.id, {
        name: msEdit.name,
        due_date: msEdit.due_date || null,
        amount: msEdit.amount === '' ? null : msEdit.amount,
        status: msEdit.status,
        published: msEdit.published,
      });
      setMsEdit(null);
      await refresh();
      toast('ok', t('Milestone updated'));
    } catch (e) { err(e, t('Could not update the milestone')); } finally { setBusy(false); }
  };

  const moveMilestone = async (index: number, delta: number) => {
    if (!engagementId) return;
    const next = [...milestones];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await contractingApi.reorderMilestones(engagementId, next.map((m) => m.id));
      await refresh();
    } catch (e) { err(e, t('Could not reorder')); void refresh(); }
  };

  // ── change requests ───────────────────────────────────────────────────────
  const openCr = async (id: number) => {
    try {
      setCrDetail(await contractingApi.changeRequest(id));
      setImpactEdit(null);
    } catch (e) { err(e, t('Could not load the change request')); }
  };

  const startImpactEdit = (cr: ChangeRequestDetail) =>
    setImpactEdit({
      impact_cost: cr.impact_cost ?? '',
      impact_days: cr.impact_days ?? '',
      impact_risk: cr.impact_risk ?? '',
      goodwill: cr.goodwill,
    });

  /** Cost the change on the request itself — the consultant's step in the CR loop (DESIGN §11). */
  const saveImpact = async () => {
    if (!crDetail || !impactEdit) return;
    setBusy(true);
    try {
      await contractingApi.updateChangeRequest(crDetail.id, {
        impact_cost: impactEdit.impact_cost === '' ? null : Number(impactEdit.impact_cost),
        impact_days: impactEdit.impact_days === '' ? null : Number(impactEdit.impact_days),
        impact_risk: impactEdit.impact_risk,
        goodwill: impactEdit.goodwill,
      });
      toast('ok', t('{ref} impact updated', { ref: crDetail.ref }));
      setImpactEdit(null);
      await refresh();
      await openCr(crDetail.id);
    } catch (e) { err(e, t('Could not save the impact')); } finally { setBusy(false); }
  };

  /**
   * Submitting publishes the CR to the client. An uncosted one asks them to agree to an unknown
   * number, so it takes a second, explicit click — the server refuses it otherwise. A GOODWILL
   * change request is deliberately uncosted and is not warned about: `no charge` is the number.
   */
  const submitCr = async (cr: ChangeRequestDetail, acknowledgeUncosted = false) => {
    if (cr.impact_cost === null && cr.impact_days === null && !cr.goodwill && !acknowledgeUncosted) {
      setSubmitTarget(cr);
      return;
    }
    try {
      await contractingApi.setChangeRequestStatus(cr.id, 'submitted', undefined, {
        acknowledge_uncosted: (acknowledgeUncosted || cr.goodwill) || undefined,
      });
      toast('ok', acknowledgeUncosted
        ? t('{ref} submitted to the client — uncosted, on your confirmation', { ref: cr.ref })
        : t('{ref} submitted to the client', { ref: cr.ref }));
      setSubmitTarget(null);
      await refresh();
      await openCr(cr.id);
    } catch (e) { err(e, t('Could not submit {ref}', { ref: cr.ref })); setSubmitTarget(null); }
  };

  const submitCrForm = async () => {
    if (!engagementId) return;
    try {
      const cr = await contractingApi.createChangeRequest({ engagement_id: engagementId, ...crForm });
      toast('ok', t('{ref} created as a draft', { ref: cr.ref }));
      setCrFormOpen(false);
      setCrForm({ title: '', description: '', origin: '', impact_cost: '', impact_days: '', impact_risk: '', goodwill: false });
      await refresh();
      await openCr(cr.id);
    } catch (e) { err(e, t('Could not create the change request')); }
  };

  const setCrStatus = async (cr: ChangeRequest, status: CrStatus) => {
    try {
      await contractingApi.setChangeRequestStatus(cr.id, status);
      toast('ok', t('{ref} → {status}', { ref: cr.ref, status: t(status) }));
      await refresh();
      if (crDetail?.id === cr.id) await openCr(cr.id);
    } catch (e) { err(e, t('Could not move {ref} to {status}', { ref: cr.ref, status: t(status) })); }
  };

  const openApprove = async (cr: ChangeRequest) => {
    setApproveGoodwill(cr.goodwill);
    setApproveAddendum(false);
    setInstructions([]);
    setApproveOpen(true);
    if (engagementId && registerRows.length === 0) {
      try { setRegisterRows((await registerApi.list(engagementId, { include_descoped: true })).rows); }
      catch (e) { err(e, t('Could not load the register')); }
    }
  };

  const doApprove = async () => {
    if (!crDetail) return;
    setBusy(true);
    try {
      const out = await contractingApi.approveChangeRequest(crDetail.id, {
        revisions: instructions, goodwill: approveGoodwill, create_addendum: approveAddendum,
      });
      toast('ok', out.addendum
        ? t('{ref} approved · {n} revision(s) applied, addendum v{v} drafted', { ref: out.cr.ref, n: out.applied.length, v: out.addendum.version_no })
        : t('{ref} approved · {n} revision(s) applied', { ref: out.cr.ref, n: out.applied.length }));
      setApproveOpen(false); setInstructions([]);
      await refresh(); await openCr(crDetail.id);
      if (out.addendum) { setVersionId(out.addendum.id); scrollToAnchor('z-doc'); }
    } catch (e) { err(e, t('Could not approve the change request')); } finally { setBusy(false); }
  };

  // ── baselines / clauses ───────────────────────────────────────────────────
  const runDiff = useCallback(async () => {
    if (!engagementId) return;
    try { setDiff(await contractingApi.diff(engagementId, diffFrom || undefined, diffTo)); }
    catch (e) { setDiff(null); err(e, t('Could not compute the diff')); }
  }, [engagementId, diffFrom, diffTo, err, t]);

  useEffect(() => { if (recordOpen && diffFrom) void runDiff(); }, [recordOpen, diffFrom, diffTo, runDiff]);

  const selectClause = async (key: string) => {
    const entry = clauses.find((c) => c.key === key);
    if (!entry) return;
    setClauseKey(key);
    setClauseNew(false);
    setClauseDraft({ key, title: entry.latest.title, body_md: entry.latest.body_md });
    try { setClauseUsage(await contractingApi.clauseUsage(key)); } catch { setClauseUsage(null); }
  };

  const saveClause = async () => {
    setBusy(true);
    try {
      if (clauseNew) {
        const row = await contractingApi.createClause(clauseDraft);
        toast('ok', t('Clause {key} v{v} created', { key: row.key, v: row.version }));
        await loadClauses();
        await selectClause(row.key);
      } else if (clauseKey) {
        const row = await contractingApi.reviseClause(clauseKey, { title: clauseDraft.title, body_md: clauseDraft.body_md });
        toast('ok', t('{key} is now v{v} — signed SOWs keep the version they printed', { key: row.key, v: row.version }));
        await loadClauses();
        await selectClause(clauseKey);
      }
      await loadMeta();
    } catch (e) { err(e, t('Could not save the clause')); } finally { setBusy(false); }
  };

  const toggleClauseActive = async () => {
    if (!clauseKey) return;
    const entry = clauses.find((c) => c.key === clauseKey);
    try {
      await contractingApi.setClauseActive(clauseKey, !(entry?.active ?? true));
      toast('ok', entry?.active ? t('{key} retired', { key: clauseKey }) : t('{key} restored', { key: clauseKey }));
      await loadClauses(); await loadMeta();
    } catch (e) { err(e, t('Could not change the clause')); }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DERIVATIONS — the page ARRANGES. Every number below is a count over rows already in the
  // payload, or a comparison of two of them. Nothing is guessed.
  // ══════════════════════════════════════════════════════════════════════════

  const toggleRule = (k: RuleKey) =>
    setOpenRules((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });

  const goRegister = (params: Parameters<typeof ROUTES.register>[0] = {}) =>
    void openRoute(ROUTES.register({ engagement: engagementId ?? undefined, ...params }));

  const candidates = readiness?.candidates ?? [];
  const candidateKeys = useMemo(() => new Set(candidates.map((c) => c.key)), [candidates]);

  /**
   * R1…R8, in DECLARATION order with severity first. Every row states its own evidence and
   * routes to the surface that owns the fix. The strings quoted below (`None recorded.`, the
   * linter's message) are the SERVER'S — this file never rewords them.
   */
  const rows: ReadyRow[] = useMemo(() => {
    if (!readiness) return [];
    const p = readiness.projection;
    const out: ReadyRow[] = [];
    const cur = readiness.currency || currency;

    // R1 · unverifiable acceptance criteria — the ONLY rule the server enforces.
    if (readiness.lint_failure_count > 0) {
      const n = readiness.lint_failure_count;
      out.push({
        rule: 'acs', weight: RULE_WEIGHT.acs, state: 'broken',
        short: t('unverifiable ACs'),
        title: <><span className="ctr-n">{n}</span> {n === 1 ? t('acceptance criterion is not verifiable as written') : t('acceptance criteria are not verifiable as written')}</>,
        routeLabel: t('Register →'),
        why: (
          <>
            {t('Issuing puts Annex A into the contract. A criterion the linter rejects cannot be proved met by anybody — not the client at UAT, not you at acceptance. The server refuses to issue until these are fixed, or until you say out loud that they go in as written.')}
          </>
        ),
        body: (
          <>
            {readiness.lint_failures.map((f) => (
              <div className="ctr-item" key={f.ac_ref}>
                <div className="ctr-itemhead">
                  <span className="src-ref">{f.ac_ref}</span> <span className="sep">·</span>{' '}
                  <span className="src-ref">{f.requirement_ref}</span>
                </div>
                <div className="ev">{f.statement}</div>
                <div className="ev-src">
                  <span className="src-ref">acceptance_criteria.statement</span>
                  <span className="sep">·</span>{t('the register speaking')}
                </div>
                {/* the linter's own words, unedited */}
                <div className="ctr-lint ctr-lint--err">{t('Linter:')} {f.errors.join(' ') || t('Fails the acceptance-criterion linter.')}</div>
              </div>
            ))}
          </>
        ),
        fix: {
          do: <>{t('Open each criterion in the register and add the number, count or duration a tester checks. Then re-save the draft.')}</>,
          control: (
            <button
              className="btn btn-sm"
              onClick={() => goRegister({
                ac: readiness.lint_failures[0]?.ac_ref,
                ref: readiness.lint_failures[0]?.requirement_ref,
                anchor: 'register-grid',
              })}
            >
              {t('Open {ref} in the Register →', { ref: readiness.lint_failures[0]?.ac_ref ?? t('the criterion') })}
            </button>
          ),
        },
      });
    }

    // R2 · nothing expressly out of scope.
    if (p.out_of_scope === 0) {
      out.push({
        rule: 'excl', weight: RULE_WEIGHT.excl, state: 'attend',
        short: t('no exclusions'),
        title: <>{t('Nothing is expressly out of scope')}</>,
        routeLabel: t('Register →'),
        why: (
          <>
            {t('The contract prints an')} <em>{t('Out of scope')}</em> {t('section whatever happens. With no requirement typed')}{' '}
            <span className="src-ref">out_of_scope</span> {t('and none descoped, that section reads')}{' '}
            <em>“None recorded.”</em> {t('— the one page a scope dispute is decided on, blank. Exclusions are first-class in the schema precisely so they can be printed.')}
          </>
        ),
        body: (
          <div className="ctr-item">
            <div className="ctr-itemhead">{t('What the section will print')}</div>
            <div className="ev">
              The following are expressly <strong>not</strong> included in this Statement of Work. …
              <br /><em>None recorded.</em>
            </div>
            <div className="ev-src">
              <span className="src-ref">sowRender.ts · buildSectionBodies · exclusions</span>
              <span className="sep">·</span>{t('the generator speaking')}
            </div>
          </div>
        ),
        fix: {
          do: <>{t('Type the things you discussed and are NOT doing as requirements of type')} <span className="src-ref">out_of_scope</span>. {t('They print as the exclusions list.')}</>,
          control: <button className="btn btn-sm" onClick={() => goRegister({ tab: 'new' })}>{t('New out-of-scope requirement →')}</button>,
        },
      });
    }

    // R3 + R4 · the priced-on sections that will print empty. One row: they share a fix.
    const emptyNarrative: string[] = [];
    if (p.assumptions === 0) emptyNarrative.push(t('Assumptions'));
    if (p.constraints === 0) emptyNarrative.push(t('Constraints'));
    if (emptyNarrative.length) {
      out.push({
        rule: 'narr', weight: RULE_WEIGHT.narr, state: 'attend',
        short: t('empty sections'),
        title: (
          <>
            <span className="ctr-n">{emptyNarrative.length}</span>{' '}
            {emptyNarrative.length === 1
              ? t('priced-on section will print empty — {names}', { names: emptyNarrative.join(', ') })
              : t('priced-on sections will print empty — {names}', { names: emptyNarrative.join(', ') })}
          </>
        ),
        routeLabel: t('Register →'),
        why: (
          <>
            {t('These sections tell the client what the price assumed and what bound the solution. Empty, they hand back every assumption you actually made. They are populated by typing requirements as')}{' '}
            <span className="src-ref">assumption</span> {t('or')}{' '}
            <span className="src-ref">constraint</span> {t('in the register — no separate form exists, and none should.')}
          </>
        ),
        body: (
          <div className="ctr-item">
            <div className="ctr-itemhead">{t('Register composition')}</div>
            <div className="ev">
              {readiness.requirements.length
                ? readiness.requirements.map((r) => `${r.ref} — ${r.type}`).join(' · ')
                : t('no requirements')}
            </div>
            <div className="ev-src">
              <span className="src-ref">requirements.type</span><span className="sep">·</span>{t('counted, not judged')}
            </div>
          </div>
        ),
        fix: {
          do: <>{t('Same surface, different type:')} <span className="src-ref">assumption</span> {t('for what the price assumed,')} <span className="src-ref">constraint</span> {t('for what was fixed.')}</>,
          control: <button className="btn btn-sm" onClick={() => goRegister({})}>{t('Open the register →')}</button>,
        },
      });
    }

    // R5 · money with nothing to accept.
    if (readiness.unfunded_milestones.length) {
      const n = readiness.unfunded_milestones.length;
      out.push({
        rule: 'ms', weight: RULE_WEIGHT.ms, state: 'attend',
        short: t('unfunded milestone'),
        title: (
          <>
            <span className="ctr-n">{n}</span>{' '}
            {n === 1 ? t('payment milestone carries money but no deliverable') : t('payment milestones carry money but no deliverable')}
          </>
        ),
        routeLabel: t('Schedule ↓'),
        why: (
          <>
            {t('An acceptance record is issued')} <em>{t('for a milestone')}</em> {t('and lists the deliverables it accepts. A milestone with an amount and no deliverable is an invoice trigger with nothing to trigger it — the client is asked to pay on the acceptance of nothing.')}
          </>
        ),
        body: (
          <>
            {readiness.unfunded_milestones.map((m) => (
              <div className="ctr-item" key={m.id}>
                <div className="ctr-itemhead">
                  <Tx type="milestone" id={m.id} field="name" original={m.name} as="span" />
                  {m.published && <span className="badge badge-plain ml-8">{t('visible to the client')}</span>}
                </div>
                <div className="ev">
                  {money(m.amount, cur)} · {t('due')} {m.due_date ? fmtDate(m.due_date) : t('no date')} ·{' '}
                  <span className="voice-figure">0</span> {t('deliverables')} ·{' '}
                  <span className="voice-figure">{m.acceptance_record_count}</span> {t('acceptance records')}
                </div>
                <div className="ev-src">
                  <span className="src-ref">milestones · deliverables.milestone_id</span>
                  <span className="sep">·</span>{t('counted')}
                </div>
              </div>
            ))}
          </>
        ),
        fix: {
          do: <>{t('Either attach a deliverable to the milestone, or fold its amount into a milestone that has one. A payment needs something to accept.')}</>,
          control: <button className="btn btn-sm" onClick={() => scrollToAnchor('z-sched')}>{t('Go to the payment schedule ↓')}</button>,
        },
      });
    }

    // R6 · blank contract variables, and what the record could fill.
    if (candidates.length) {
      const stillBlank = readiness.blank_variables
        .filter((k) => !candidateKeys.has(k))
        .map((k) => (meta?.variables ?? []).find((v) => v.key === k)?.label ?? k);
      out.push({
        rule: 'vars', weight: RULE_WEIGHT.vars, state: 'attend',
        short: t('blank variables'),
        title: (
          <>
            <span className="ctr-n">{candidates.length}</span>{' '}
            {candidates.length === 1 ? t('contract variable the record can fill') : t('contract variables the record can fill')}
            {stillBlank.length ? <> · {t('{n} only you can', { n: stillBlank.length })}</> : null}
          </>
        ),
        routeLabel: t('Fill ↓'),
        why: (
          <>
            {t('A new version renders with every variable outside the defaults blank. These values are already columns in this database; retyping them is how a signature block ends up naming a company where it should name a person.')}
          </>
        ),
        body: (
          <>
            {candidates.map((c) => {
              const label = (meta?.variables ?? []).find((v) => v.key === c.key)?.label ?? c.key;
              const already = (vars[c.key] ?? '') || staged[c.key] || '';
              return (
                <div className="ctr-cand" key={c.key}>
                  <div className="ctr-cand-k">{label}</div>
                  <div className="ctr-cand-v">
                    <span className="ctr-cand-val">{c.value}</span>
                    <span className="ctr-cand-src">{t('from')} <span className="src-ref">{c.source_label}</span></span>
                  </div>
                  <div>
                    {already ? (
                      <span className="ctr-tag">{staged[c.key] ? t('staged') : t('in the document')}</span>
                    ) : version && version.status !== 'draft' ? (
                      <span className="ctr-muted">{t('v{n} is frozen', { n: version.version_no })}</span>
                    ) : (
                      <button className="btn btn-sm" disabled={busy} onClick={() => void useCandidate(c)}>{t('Use')}</button>
                    )}
                  </div>
                </div>
              );
            })}
            {stillBlank.length > 0 && (
              <div className="ctr-itemhead" style={{ marginTop: 12 }}>
                {t('No column holds these — you type them:')}{' '}
                {stillBlank.map((l) => <span className="ctr-tag" key={l}>{l}</span>)}
              </div>
            )}
          </>
        ),
        fix: {
          do: <>{t('Each value above names the column it came from. Accepting one copies it into')} <span className="src-ref">variable_values</span> {t('— nothing is inferred and nothing is typed twice.')}</>,
        },
      });
    }

    // R7 · the milestone total against the engagement estimate.
    if (readiness.estimate_value !== null) {
      const agrees = Number(readiness.estimate_value) === Number(readiness.planned_value);
      out.push({
        rule: 'value', weight: RULE_WEIGHT.value, state: agrees ? 'ok' : 'attend',
        short: t('value mismatch'),
        title: agrees
          ? <>{t('Milestone total {total} matches the engagement estimate', { total: money(readiness.planned_value, cur) })}</>
          : <>{t('Milestone total {total} does not match the engagement estimate {estimate}', { total: money(readiness.planned_value, cur), estimate: money(readiness.estimate_value, cur) })}</>,
        routeLabel: agrees ? null : t('Schedule ↓'),
        why: agrees
          ? <>{t('The figure the SOW prints and the figure gate G2 reads are the same number.')}</>
          : (
            <>
              {t('G2 reads whichever is present; the SOW prints the milestone sum. Two different numbers describe this engagement and the contract will print one of them without saying so.')}
            </>
          ),
        body: (
          <div className="ctr-item">
            <div className="ctr-itemhead">{t('Both figures')}</div>
            <div className="ev">
              {t('milestones sum')} <span className="voice-figure">{money(readiness.planned_value, cur)}</span> ·{' '}
              <span className="src-ref">engagements.estimate_value</span>{' '}
              <span className="voice-figure">{money(readiness.estimate_value, cur)}</span>
            </div>
            <div className="ev-src">
              <span className="src-ref">milestones.amount · engagements.estimate_value</span>
              <span className="sep">·</span>{t('added, not judged')}
            </div>
          </div>
        ),
        fix: agrees ? null : {
          do: <>{t('Decide which number is true and make the other match. The contract prints the milestone sum; G2 reads whichever it finds.')}</>,
          control: <button className="btn btn-sm" onClick={() => scrollToAnchor('z-sched')}>{t('Go to the payment schedule ↓')}</button>,
        },
      });
    }

    // R8 · draft requirements. G1's own condition string is quoted where the gate answered it,
    // rather than recounted here — the F6 law: read the gate's string, never recount it.
    const drafts = readiness.draft_requirements;
    const total = readiness.requirements.length;
    out.push({
      rule: 'reg', weight: RULE_WEIGHT.reg, state: drafts ? 'attend' : 'ok',
      short: t('draft requirements'),
      title: drafts
        ? <><span className="ctr-n">{drafts}</span> {t('of {total} requirements are still draft', { total })}</>
        : <>{t('{total} of {total} requirements held', { total })}</>,
      routeLabel: drafts ? t('Register →') : null,
      why: drafts
        ? <>{t('A draft requirement prints into the scope table exactly like a held one. G1 has not been cleared for these.')}</>
        : <>{t('Every row the scope section will print has been committed by a human.')}</>,
      body: (
        <div className="ctr-item">
          <div className="ctr-itemhead">{t('Scope table, as it will print')}</div>
          {readiness.requirements.map((r) => (
            <div key={r.ref} style={{ marginBottom: 8 }}>
              <div className="ctr-itemhead">
                <span className="src-ref">{r.ref}</span> · {t(r.priority)} · {t(REQUIREMENT_STATUS_LABEL[r.status]?.word ?? r.status)}
              </div>
              <div className="ev">{r.statement}</div>
            </div>
          ))}
          <div className="ev-src">
            <span className="src-ref">requirement_revisions.statement</span>
            <span className="sep">·</span>{t('the register speaking')}
          </div>
        </div>
      ),
      fix: drafts ? {
        do: <>{t('Confirm them, or drop them. A draft row prints into the scope table looking exactly like a committed one.')}</>,
        control: <button className="btn btn-sm" onClick={() => goRegister({ status: 'draft' })}>{t('Open the register, filtered to draft →')}</button>,
      } : null,
    });

    // Severity first, declaration order within — never a ranking that depends on the values.
    const sev = { broken: 0, attend: 1, ok: 2 };
    return out.sort((a, b) => (sev[a.state] - sev[b.state]) || (a.weight - b.weight));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readiness, meta, currency, vars, staged, version, busy, candidates, candidateKeys, t, fmtDate]);

  const unmet = rows.filter((r) => r.state !== 'ok');
  const blocking = rows.filter((r) => r.state === 'broken');
  const attending = rows.filter((r) => r.state === 'attend');
  const met = rows.filter((r) => r.state === 'ok');

  // ── the rail ──────────────────────────────────────────────────────────────
  const rail = useMemo<CtrRailCell[]>(() => {
    const v = headVersion;
    const p = readiness?.projection ?? null;
    const gate = readiness?.gate ?? null;
    const cur = readiness?.currency ?? currency;

    const auto = gate?.conditions.filter((c) => c.kind === 'auto') ?? [];
    const manual = gate?.conditions.filter((c) => c.kind === 'manual') ?? [];

    const cells: CtrRailCell[] = [
      {
        key: 'DOCUMENT',
        value: v ? `v${v.version_no} ${t(v.status)}` : '—',
        unit: null,
        detail: v ? `${short10(v.content_hash)} · ${fmtDate(v.signed_at ?? v.issued_at)}` : t('no SOW exists'),
        tone: v ? (v.status === 'signed' ? 'ok' : v.status === 'issued' ? 'ink' : 'nil') : 'nil',
        route: null,
      },
      {
        key: 'WOULD PRINT',
        value: p ? t('{n} in scope', { n: p.in_scope }) : '—',
        unit: null,
        detail: p ? t('{must} must · {excluded} excluded', { must: p.must, excluded: p.out_of_scope }) : t('the pre-flight did not answer'),
        tone: p ? 'ink' : 'unknown',
        route: null,
      },
      {
        key: 'VALUE',
        value: p ? money(p.planned_value, cur) : '—',
        unit: null,
        detail: !readiness ? t('the pre-flight did not answer')
          : readiness.estimate_value === null ? t('no estimate on record')
            : Number(readiness.estimate_value) === Number(readiness.planned_value) ? t('= engagement estimate')
              : t('estimate {estimate}', { estimate: money(readiness.estimate_value, cur) }),
        tone: !readiness ? 'unknown'
          : readiness.estimate_value !== null && Number(readiness.estimate_value) !== Number(readiness.planned_value)
            ? 'attend' : 'ink',
        route: null,
      },
      {
        key: 'NOT FIT TO SIGN',
        value: readiness ? String(unmet.length) : '—',
        unit: null,
        // One item, chosen by the fixed rule order — not a run whose length varies with the data.
        detail: !readiness ? t('the pre-flight did not answer')
          : unmet.length ? cutRun(unmet.map((r) => r.short))
            : t('nothing outstanding'),
        tone: !readiness ? 'unknown' : blocking.length ? 'broken' : unmet.length ? 'attend' : 'ok',
        route: null,
      },
      {
        key: 'G3 · SOW SIGNED',
        value: gate ? t('{met} of {total}', { met: gate.metCount, total: gate.total }) : '—',
        unit: null,
        detail: gate
          ? t('{autoMet}/{autoTotal} auto · {manMet}/{manTotal} manual', {
            autoMet: auto.filter((c) => c.met).length, autoTotal: auto.length,
            manMet: manual.filter((c) => c.met).length, manTotal: manual.length,
          })
          : t('gate engine did not answer'),
        tone: !gate ? 'unknown' : gate.metCount === gate.total ? 'ok' : 'ink',
        route: null,
      },
    ];
    return cells;
  }, [headVersion, readiness, currency, unmet, blocking, t, fmtDate]);

  /**
   * Where each cell goes. A rail cell is a READOUT first; the destination is named only on
   * hover, so five identical "open" hints do not compete with the five figures.
   */
  const railGo: Record<CtrRailKey, { label: string; run: () => void }> = {
    DOCUMENT: { label: 'document', run: () => scrollToAnchor('z-doc') },
    'WOULD PRINT': { label: 'register', run: () => goRegister({}) },
    VALUE: { label: 'schedule', run: () => scrollToAnchor('z-sched') },
    'NOT FIT TO SIGN': { label: 'the list', run: () => scrollToAnchor('z-ready') },
    'G3 · SOW SIGNED': {
      label: 'lifecycle',
      run: () => void openRoute(ROUTES.lifecycle({ engagement: engagementId ?? undefined, anchor: 'gate' })),
    },
  };

  // ── the pipeline ──────────────────────────────────────────────────────────
  const pipeAt = !headVersion ? 0
    : headVersion.status === 'draft' ? 1
      : headVersion.status === 'issued' ? 2 : 3;
  const pipeSteps = [
    { k: t('Register'), v: readiness ? t('{n} in scope', { n: readiness.projection.in_scope }) : '—' },
    { k: t('Draft'), v: headVersion ? `v${headVersion.version_no} · ${short(headVersion.content_hash)}` : t('not created') },
    { k: t('Issued'), v: headVersion?.issued_at ? fmtDateTime(headVersion.issued_at) : '—' },
    { k: t('Signed'), v: headVersion?.signed_at ? fmtDateTime(headVersion.signed_at) : '—' },
    { k: t('Baseline'), v: baselines[0] ? short(baselines[0].content_hash) : '—' },
  ];

  // ── the section map ───────────────────────────────────────────────────────
  // The LIST is the server's (meta.sections, or the version's own once one exists), so the map
  // can never name a section the document does not have. The one-line summary beside each is
  // derived from the same projection the document renders from.
  const sectionList = useMemo(
    () => (version?.sections?.length ? version.sections : (meta?.sections ?? []).map((s) => ({ ...s, overridden: false }))),
    [version, meta],
  );

  const sectionStat = (key: string): { text: string; tone: '' | 'attend' | 'broken' } => {
    const p = readiness?.projection;
    if (!p) return { text: '—', tone: '' };
    const filled = (k: string) => !!((vars[k] ?? '').trim() || staged[k]);
    switch (key) {
      case 'parties':
        return {
          text: `${readiness.parties.legal_name || readiness.parties.client_name} · ${readiness.parties.sponsor_name ?? t('no contact nominated')}`,
          tone: readiness.parties.sponsor_name ? '' : 'attend',
        };
      case 'background': return { text: filled('background') ? t('set') : t('no background recorded'), tone: '' };
      case 'scope': return { text: t('{count} requirements · {groups}', { count: p.in_scope, groups: tp(p.scope_groups.length, '{n} group', '{n} groups') }), tone: '' };
      case 'exclusions': return p.out_of_scope
        ? { text: t('{n} excluded', { n: p.out_of_scope }), tone: '' }
        : { text: 'None recorded.', tone: 'attend' };
      case 'deliverables': return p.deliverables
        ? { text: tp(p.deliverables, '{n} deliverable', '{n} deliverables'), tone: '' }
        : { text: 'None recorded.', tone: 'attend' };
      case 'assumptions': return p.assumptions ? { text: String(p.assumptions), tone: '' } : { text: 'None recorded.', tone: 'attend' };
      case 'constraints': return p.constraints ? { text: String(p.constraints), tone: '' } : { text: 'None recorded.', tone: 'attend' };
      case 'schedule': return { text: tp(p.milestones, '{n} milestone', '{n} milestones'), tone: '' };
      case 'fees': return { text: money(p.planned_value, readiness.currency), tone: '' };
      case 'acceptance': return { text: t('{n} working days', { n: readiness.review_window_days ?? '—' }), tone: '' };
      case 'annex_acceptance_criteria':
        return p.acceptance_criteria
          ? {
            text: readiness.lint_failure_count
              ? t('{n} criteria · {bad} unverifiable', { n: p.acceptance_criteria, bad: readiness.lint_failure_count })
              : t('{n} criteria', { n: p.acceptance_criteria }),
            tone: readiness.lint_failure_count ? 'broken' : '',
          }
          : { text: 'None recorded.', tone: 'attend' };
      case 'signatures':
        return filled('client_signatory')
          ? { text: t('named'), tone: '' }
          : { text: t('client name in place of a person'), tone: 'attend' };
      default: return { text: '', tone: '' };
    }
  };

  // ── the verdict ───────────────────────────────────────────────────────────
  const varsFilled = (meta?.variables ?? []).filter((d) => ((vars[d.key] ?? '').trim() || d.default || staged[d.key])).length;
  const openCrCount = (crTotals.draft ?? 0) + (crTotals.submitted ?? 0);
  const hasBaseline = baselines.length > 0;
  const crEditable = !!crDetail && !['approved', 'rejected', 'withdrawn'].includes(crDetail.status);
  const crUncosted = !!crDetail && crDetail.impact_cost === null && crDetail.impact_days === null && !crDetail.goodwill;
  const activeClause = clauses.find((c) => c.key === clauseKey);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!current) {
    return (
      <div className="page">
        <header className="mast"><div className="mast-top"><div className="mast-main">
          <h1>{t('Contracting')}</h1>
        </div></div></header>
        <EmptyState
          kind="empty"
          title={t('No engagement selected')}
          detail={t('Pick an engagement in the top bar — a Statement of Work is always scoped to one.')}
        />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="page ctr-page">
      {/* ── MASTHEAD ─────────────────────────────────────────────────────── */}
      <header className="mast">
        <div className="mast-top"><div className="mast-main">
        <div className="u-label ctr-kicker">{t('Contracting')}</div>
        <h1>{current.name}</h1>
        <div className="mast-sub ctr-mast-sub">
          <span className="src-ref">{current.code}</span>
          {readiness && <> · {readiness.parties.client_name}</>}
          {readiness?.parties.legal_name && readiness.parties.legal_name !== readiness.parties.client_name && (
            <> · {t('contracting as {name}', { name: readiness.parties.legal_name })}</>
          )}
          {readiness?.parties.city && <> · {readiness.parties.city}{readiness.parties.country ? `, ${readiness.parties.country}` : ''}</>}
          {poll.notice && <> · <span className="text-ink-3">{poll.notice}</span></>}
        </div>
        </div></div>
      </header>

      {/* ── THE RAIL — five cells on the instrument sheet, riding the masthead rule ── */}
      <div className="instr">
        <div className="rail">
          {rail.map((cell) => (
            <button
              key={cell.key}
              type="button"
              className="fld"
              title={t('Open the {target}', { target: t(railGo[cell.key].label) })}
              onClick={railGo[cell.key].run}
            >
              <div className="fl u-label">
                {t(cell.key)}
                <span className="go" aria-hidden="true">{t(railGo[cell.key].label)}</span>
              </div>
              <div className={`fv voice-figure${TONE_CLASS[cell.tone] ?? ''}`}>
                {cell.tone === 'unknown'
                  ? <span className="n-unknown" title={cell.detail}>—</span>
                  : cell.value}
              </div>
              <div className="fd">{cell.detail}</div>
            </button>
          ))}
        </div>

        {/* ── THE VERDICT — one sentence, and the page's ONE filled control,
               INSIDE the instrument sheet (a control never sits on the field) ── */}
        <div className="instr-band ctr-verdict">
        <div className="ctr-verdict-txt">
          {poll.loading && !data ? (
            <span className="text-ink-3">{t('Reading the register…')}</span>
          ) : !readiness ? (
            <>{t('The pre-flight could not be computed, so this page cannot say whether the register is fit to print a contract.')}{' '}
              <span className="text-ink-3">{readinessError}</span></>
          ) : !headVersion ? (
            blocking.length ? (
              <>
                {t('The register is not yet fit to print a contract —')} <strong>{blocking.length}</strong>{' '}
                {blocking.length === 1 ? t('hard stop,') : t('hard stops,')} <strong>{attending.length}</strong>{' '}
                {attending.length === 1 ? t('thing worth fixing first.') : t('things worth fixing first.')}
                <div className="ctr-verdict-sub">
                  {t('You can create the document now; the server will refuse to')} <em>{t('issue')}</em> {t('it while the hard stops stand.')}
                </div>
              </>
            ) : (
              <>{t('No Statement of Work exists. The register would print')}{' '}
                <strong>{readiness.projection.in_scope}</strong> {t('requirements into one.')}</>
            )
          ) : headVersion.status === 'draft' ? (
            <>
              <ClaimMark status="draft" /> <strong>v{headVersion.version_no}</strong> {t('is a draft. Nothing has left the building.')}
              <div className="ctr-verdict-sub">
                {t('Issuing re-renders without the draft marker, publishes to the portal and writes the PDF.')}
              </div>
            </>
          ) : headVersion.status === 'issued' ? (
            <>
              <ClaimMark status="issued" /> <strong>v{headVersion.version_no}</strong>{' '}
              {t('is with the client, issued {when}. It is the hash they will sign:', { when: fmtDateTime(headVersion.issued_at) })}{' '}
              <span className="src-ref">{short(headVersion.content_hash)}</span>.
              <div className="ctr-verdict-sub">
                {t('Recording the signature freezes the register as an immutable baseline. After that, scope moves only through a change request.')}
              </div>
            </>
          ) : (
            <>
              <ClaimMark status="signed" /> <strong>v{headVersion.version_no}</strong> {t('signed')}{' '}
              {fmtDateTime(headVersion.signed_at)}
              {headVersion.signature ? <> {t('by {name}', { name: headVersion.signature.typed_name })}</> : null}.
              {baselines[0] && (
                <> {t('Baseline')} <span className="src-ref">{short(baselines[0].content_hash)}</span> {t('holds')}{' '}
                  {tp(baselines[0].requirement_count, '{n} requirement', '{n} requirements')}.</>
              )}
              <div className="ctr-verdict-sub">
                {t('G3 is cleared on the record. The only route through the baseline is a change request.')}
              </div>
            </>
          )}
        </div>
        <div className="ctr-verdict-act">
          {!headVersion ? (
            <button className="btn btn-accent" disabled={busy} onClick={() => setNewSowOpen(true)}>
              {t('Create the SOW')}
            </button>
          ) : headVersion.status === 'draft' ? (
            <button
              className="btn btn-accent"
              disabled={busy || dirty || !version || version.id !== headVersion.id}
              title={dirty ? t('Save the draft first — the preview is behind your edits') : undefined}
              onClick={() => version && void openIssue(version)}
            >
              <Send className="icon-sm" /> {t('Issue to the client')}
            </button>
          ) : headVersion.status === 'issued' ? (
            <button className="btn btn-accent" disabled={busy} onClick={() => { setVersionId(headVersion.id); setSignOpen(true); }}>
              <PenLine className="icon-sm" /> {t('Record the signature')}
            </button>
          ) : (
            <button className="btn btn-accent" onClick={() => setCrFormOpen(true)}>
              <Plus className="icon-sm" /> {t('Raise a change request')}
            </button>
          )}
        </div>
        </div>
      </div>

      {/* ══ ZONE 1 · FIT TO SIGN ══════════════════════════════════════════ */}
      <section className="zone" id="z-ready">
        <div className="zone-head">
          <span className="u-label">
            {!headVersion ? t('Fit to sign')
              : headVersion.status === 'draft' ? t('Fit to sign — v{n} draft', { n: headVersion.version_no })
                : headVersion.status === 'issued' ? t('Fit to sign — what a v{n} would carry', { n: headVersion.version_no + 1 })
                  : t('Fit to sign — v{n} is frozen; this is what an addendum would carry', { n: headVersion.version_no })}
          </span>
          <span className="zone-note">{t('every row states its own evidence and routes to the surface that owns the fix')}</span>
        </div>

        {readiness?.problems.length ? (
          <ProblemState problems={readiness.problems} source="gates" zone={t('The gate tallies')} onRetry={() => void refresh()} />
        ) : null}

        {poll.loading && !data ? (
          <div className="empty-state-card"><div className="empty-state"><div className="spinner" /></div></div>
        ) : !readiness ? (
          <EmptyState
            kind="error"
            title={t('The pre-flight did not answer')}
            detail={readinessError ?? t('GET /api/contracting/readiness failed.')}
            onRetry={() => void refresh()}
          />
        ) : (
          <>
            <div className="ctr-split">
              <span>
                <span className="ctr-lamp ctr-lamp--broken" />
                <b className="voice-figure">{blocking.length}</b> {blocking.length === 1 ? t('blocks issuing — the server refuses') : t('block issuing — the server refuses')}
              </span>
              <span>
                <span className="ctr-lamp ctr-lamp--attend" />
                <b className="voice-figure">{attending.length}</b> {t('worth fixing — nothing stops you')}
              </span>
              <span>
                <span className="ctr-lamp ctr-lamp--ok" />
                <b className="voice-figure">{met.length}</b> {t('met')}
              </span>
            </div>

            {rows.map((r) => {
              const open = openRules.has(r.rule);
              return (
                <div key={r.rule} className={`ctr-rd ctr-rd--${r.state}${open ? ' is-open' : ''}`}>
                  <button className="ctr-rd-head" aria-expanded={open} onClick={() => toggleRule(r.rule)}>
                    <span className={`ctr-lamp ctr-lamp--${r.state}`} />
                    <span className="ctr-rd-title">{r.title}</span>
                    {r.routeLabel && <span className="ctr-rd-route">{r.routeLabel}</span>}
                    <ChevronRight className="icon-sm ctr-rd-chev" style={open ? { transform: 'rotate(90deg)' } : undefined} />
                  </button>
                  {open && (
                    <div className="ctr-rd-body">
                      <div>
                        <p className="ctr-why">{r.why}</p>
                        {r.body}
                      </div>
                      <div className="ctr-rd-fix">
                        <span className="u-label">{r.state === 'ok' ? t('Why it is met') : t('The fix')}</span>
                        {r.fix ? (
                          <>
                            <p>{r.fix.do}</p>
                            {r.fix.control}
                          </>
                        ) : (
                          <p className="ctr-muted">{t('Nothing to do — this condition is satisfied by the rows on the left.')}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* G3 read as a STRING, never recounted (F6). The gate engine's own words. */}
            {readiness.gate && (
              <div className="ctr-gate">
                <div className="ctr-itemhead">
                  <span className="src-ref">{readiness.gate.gate}</span> {readiness.gate.title} —{' '}
                  <span className="voice-figure">{readiness.gate.metCount}</span> {t('of')}{' '}
                  <span className="voice-figure">{readiness.gate.total}</span> {t('conditions met')}
                </div>
                <ul className="ctr-gate-list">
                  {readiness.gate.conditions.map((c) => (
                    <li key={`${c.kind}-${c.label}`} className={c.met ? 'is-met' : ''}>
                      <span className={`ctr-lamp ctr-lamp--${c.met ? 'ok' : 'attend'}`} />
                      <span className="ctr-gate-l">{c.label}</span>
                      <span className="ctr-gate-d">{c.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {/* ══ ZONE 2 · THE DOCUMENT ═════════════════════════════════════════ */}
      <section className="zone" id="z-doc">
        <div className="zone-head">
          <span className="u-label">{t('The document')}</span>
          <span className="zone-note">
            {headVersion
              ? `v${headVersion.version_no} · ${t(headVersion.status)} · ${tp(headVersion.override_count, '{n} section override', '{n} section overrides')} · ${short(headVersion.content_hash)}`
              : t('not created — this is what it would contain')}
          </span>
        </div>

        {/* the pipeline — weight and a well, never a hue */}
        <div className="ctr-pipe">
          {pipeSteps.map((s, i) => (
            <div key={s.k} className={`ctr-pipe-step${i < pipeAt ? ' is-done' : ''}${i === pipeAt ? ' is-now' : ''}`}>
              <div className="ctr-pipe-k">{s.k}</div>
              <div className="ctr-pipe-v">{s.v}</div>
            </div>
          ))}
        </div>

        {allVersions.length > 1 && (
          <div className="ctr-verlist">
            <span className="u-label">{t('Versions')}</span>
            {allVersions.map((v) => (
              <button
                key={v.id}
                className={`ctr-verchip${versionId === v.id ? ' is-sel' : ''}`}
                onClick={() => setVersionId(v.id)}
              >
                <ClaimMark status={v.status} /> v{v.version_no}
                {v.is_addendum && <span className="ctr-tag">{t('addendum')}</span>}
                <span className="ctr-muted">{t(v.status)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="ctr-doc">
          <div className="ctr-doc-left">
            {/* Variables */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="card-title">{t('Variables')}</h3>
                <span className="ctr-fill">
                  <span className="voice-figure">{varsFilled}</span> {t('of')}{' '}
                  <span className="voice-figure">{(meta?.variables ?? []).length}</span> {t('have a value')}
                </span>
              </div>
              <div className="card-b">
                {(['narrative', 'commercial', 'schedule', 'parties'] as const).map((group) => {
                  const defs = (meta?.variables ?? []).filter((v) => v.group === group);
                  const set = defs.filter((d) => ((vars[d.key] ?? '').trim() || d.default || staged[d.key])).length;
                  return (
                    <div key={group}>
                      <div className="ctr-grpline">
                        <span className="u-label">{t(group)}</span>
                        <span className="ctr-cnt voice-figure">{t('{set} of {total}', { set, total: defs.length })}</span>
                      </div>
                      {defs.map((def) => {
                        const value = (vars[def.key] ?? '').trim() || staged[def.key] || '';
                        const loud = !value && (candidateKeys.has(def.key)
                          || def.key === 'client_signatory' || def.key === 'supplier_signatory');
                        if (isDraft) {
                          return (
                            <div className="form-group" key={def.key}>
                              <label className="form-label" htmlFor={`var-${def.key}`}>{def.label}</label>
                              {def.kind === 'longtext' ? (
                                <textarea
                                  id={`var-${def.key}`} className="form-textarea" rows={4}
                                  value={vars[def.key] ?? ''} onChange={(e) => setVar(def.key, e.target.value)}
                                />
                              ) : (
                                <input
                                  id={`var-${def.key}`} className="form-input"
                                  type={def.kind === 'date' ? 'date' : 'text'}
                                  value={vars[def.key] ?? ''} onChange={(e) => setVar(def.key, e.target.value)}
                                />
                              )}
                              {!value && candidateKeys.has(def.key) && (
                                <div className="ctr-blanknote">{t('blank — the record offers a value in')} <em>{t('Fit to sign')}</em> ↑</div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div className="ctr-fieldrow" key={def.key}>
                            <div className="ctr-fl">{def.label}</div>
                            <div className={`ctr-fv${value ? '' : loud ? ' is-blank' : ' is-quiet'}`}>
                              {value || (candidateKeys.has(def.key)
                                ? t('blank — the record offers a value ↑')
                                : loud ? t('blank — the signature block degrades without it')
                                  : def.default || '—')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {isDraft && (
                  <div className="ctr-cardverbs">
                    <button className="btn btn-sm" disabled={busy || !dirty} onClick={() => void saveDraft()}>
                      <Save className="icon-sm" /> {t('Save & re-render')}
                    </button>
                    {dirty && (
                      <span className="ctr-dirty">
                        <AlertTriangle className="icon-sm icon-inline" /> {t('Unsaved edits — the preview still shows the last render.')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Clauses */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="card-title">{t('Clauses')}</h3>
                <span className="ctr-fill">
                  {version
                    ? t('{n} pinned at the version this document printed', { n: version.clause_versions.length })
                    : t('{n} would print, at their latest version', { n: clauses.filter((c) => c.active).length })}
                </span>
              </div>
              <div className="card-b">
                {clauses.filter((c) => c.active).map((c) => {
                  const pinned = version?.clause_versions.find((p) => p.key === c.key);
                  const stale = !!pinned && pinned.version < c.latest.version;
                  return (
                    <label key={c.key} className="ctr-clauserow" style={{ cursor: isDraft ? 'pointer' : 'default' }}>
                      <input
                        type="checkbox" disabled={!isDraft}
                        checked={version ? clauseKeys.includes(c.key) : true}
                        onChange={() => toggleClause(c.key)}
                      />
                      <span className="ctr-ck">{c.title}</span>
                      <span className="ctr-cv voice-figure">v{pinned ? pinned.version : c.latest.version}</span>
                      {stale && <span className="ctr-tag ctr-tag--attend">{t('v{n} available', { n: c.latest.version })}</span>}
                    </label>
                  );
                })}
                <div className="ctr-cardverbs">
                  {isDraft && (
                    <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void saveDraft({ repin_clauses: true })}>
                      <Undo2 className="icon-sm" /> {t('Re-pin to latest')}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => { setRecordOpen(true); scrollToAnchor('z-record'); }}
                  >
                    {t('Edit the library →')}
                  </button>
                </div>
                <div className="ctr-note">{t('A signed SOW keeps the clause version it printed.')}</div>
              </div>
            </div>

            {/* Sections — what each will print */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="card-title">{t('Sections')}</h3>
                <span className="ctr-fill">{t('what each will print')}</span>
              </div>
              <div className="card-b">
                <div className="ctr-note mb-8">
                  {t('An override replaces the generated section entirely and is flagged on every regeneration. Clear the box to go back to the projection.')}
                </div>
                {sectionList.map((s, i) => {
                  const st = sectionStat(s.key);
                  const open = openSection === s.key;
                  const has = !!(overrides[s.key] && overrides[s.key].trim());
                  return (
                    <div key={s.key}>
                      <button
                        className={`ctr-secrow${open ? ' is-sel' : ''}`}
                        onClick={() => setOpenSection(open ? null : s.key)}
                      >
                        <span className="ctr-sn voice-figure">{i + 1}</span>
                        <span className="ctr-st">{s.title}</span>
                        <span className={`ctr-sw${st.tone ? ` ctr-sw--${st.tone}` : ''}`}>{has ? t('overridden') : st.text}</span>
                      </button>
                      {open && (
                        <textarea
                          className="form-textarea mt-4" rows={6} disabled={!isDraft}
                          placeholder={t('Leave empty to use the generated text.')}
                          value={overrides[s.key] ?? ''} onChange={(e) => setOverride(s.key, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* the canonical preview */}
          <div className="card ctr-preview">
            <div className="card-header flex items-center justify-between">
              <h3 className="card-title">{t('Document preview')}</h3>
              <span className="ctr-fill">{t('rendered from the register · this HTML is canonical · paper in both themes, because it prints')}</span>
            </div>
            {version ? (
              <>
                <div className="ctr-previewbar">
                  <span className="src-ref" title={version.content_hash ?? ''}>{short(version.content_hash)}</span>
                  <a href={contractingApi.htmlUrl(version.id)} target="_blank" rel="noreferrer" className="text-accent">
                    {t('open HTML')} <ExternalLink className="icon-sm icon-inline" />
                  </a>
                  {isDraft && (
                    <a
                      href={contractingApi.issuedHtmlUrl(version.id)} target="_blank" rel="noreferrer" className="text-accent"
                      title={t('Rendered without the draft marker or the lint notes — what the client would receive')}
                    >
                      {t('as the client sees it')} <ExternalLink className="icon-sm icon-inline" />
                    </a>
                  )}
                  {version.pdf_key && (
                    <a href={contractingApi.pdfUrl(version.id)} target="_blank" rel="noreferrer" className="text-accent">
                      PDF <ExternalLink className="icon-sm icon-inline" />
                    </a>
                  )}
                  {version.baseline && (
                    <span className="ctr-muted">
                      {t('baseline')} <span className="src-ref">#{version.baseline.id}</span>{' '}
                      <span className="src-ref" title={version.baseline.content_hash}>{short(version.baseline.content_hash)}</span>
                    </span>
                  )}
                  <span className="ctr-previewbar-end">
                    {headVersion && (
                      <button className="btn btn-sm" disabled={busy} onClick={() => void newVersion(sows[sows.length - 1].id)}>
                        <Plus className="icon-sm" /> {t('New version from register')}
                      </button>
                    )}
                    {isDraft && (
                      <button
                        className="btn btn-sm btn-ghost"
                        title={t('Delete this draft — it has never been issued')}
                        onClick={() => void askDelete('sow_version', version.id, t('Draft v{n}', { n: version.version_no }))}
                      >
                        <Trash2 className="icon-sm" /> {t('Discard draft')}
                      </button>
                    )}
                  </span>
                </div>
                <iframe
                  title={`SOW v${version.version_no}`}
                  className="ctr-frame"
                  srcDoc={version.rendered_html ?? `<p>${t('Not rendered yet.')}</p>`}
                />
              </>
            ) : (
              <div className="card-b">
                <p className="read">
                  {t('No document has been rendered yet. The section map on the left is what a first version would print, read from the live register — the same projection the generator uses. Creating the SOW renders it and nothing else moves.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ ZONE 3 · PAYMENT SCHEDULE ═════════════════════════════════════ */}
      <section className="zone" id="z-sched">
        <div className="zone-head">
          <span className="u-label">{t('Payment schedule')}</span>
          <span className="zone-note">
            <span className="voice-figure">{money(plannedValue, currency)}</span> {t('across')}{' '}
            {tp(milestones.length, '{n} milestone', '{n} milestones')} {t('— the figure the SOW prints and the figure G2 reads')}
          </span>
        </div>

        {poll.error && !poll.lastOkAt ? (
          <EmptyState kind="error" title={t('The payment schedule did not load')} detail={poll.error} onRetry={() => void refresh()} />
        ) : milestones.length === 0 ? (
          <EmptyState
            kind="empty"
            title={t('No milestones yet')}
            detail={t('G2 needs at least one priced milestone as the estimate, and the SOW prints this table as its payment schedule.')}
          >
            <button className="btn" onClick={() => setMsAddOpen(true)}><Plus className="icon-sm" /> {t('Add the first milestone')}</button>
          </EmptyState>
        ) : (
          <div className="card">
            <div className="ctr-tscroll">
              <table className="tbl ctr-t">
                <thead>
                  <tr>
                    <th style={{ width: 62 }} />
                    <th>{t('Milestone')}</th>
                    <th style={{ width: 118 }}>{t('Due')}</th>
                    <th className="num" style={{ width: 118 }}>{t('Amount')}</th>
                    <th style={{ width: 120 }}>{t('Status')}</th>
                    <th style={{ width: 210 }}>{t('Accepts')}</th>
                    <th style={{ width: 96 }}>{t('Client')}</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((m, i) => (
                    msEdit?.id === m.id ? (
                      <tr key={m.id} className="ctr-row-edit">
                        <td className="src-ref ctr-ord">{i + 1}</td>
                        <td>
                          <input
                            className="form-input" aria-label={t('Milestone name')} value={msEdit.name}
                            onChange={(e) => setMsEdit({ ...msEdit, name: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="form-input" type="date" aria-label={t('Due date')} value={msEdit.due_date}
                            onChange={(e) => setMsEdit({ ...msEdit, due_date: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="form-input" type="number" step="1000" aria-label={t('Amount')} value={msEdit.amount}
                            onChange={(e) => setMsEdit({ ...msEdit, amount: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select" aria-label={t('Status')} value={msEdit.status}
                            onChange={(e) => setMsEdit({ ...msEdit, status: e.target.value })}
                          >
                            {MILESTONE_STATUSES.map((s) => <option key={s} value={s}>{t(s.replace('_', ' '))}</option>)}
                          </select>
                        </td>
                        <td className="ctr-muted">{tp(m.deliverable_count, '{n} deliverable', '{n} deliverables')}</td>
                        <td>
                          <label className="ctr-check">
                            <input
                              type="checkbox" checked={msEdit.published}
                              onChange={(e) => setMsEdit({ ...msEdit, published: e.target.checked })}
                            />
                            <span>{msEdit.published ? t('visible') : t('hidden')}</span>
                          </label>
                        </td>
                        <td>
                          <div className="ctr-rowverbs">
                            <button className="btn btn-sm" disabled={busy} onClick={() => void saveMilestone()}>{t('Save')}</button>
                            <button className="btn btn-sm btn-ghost" onClick={() => setMsEdit(null)}>{t('Cancel')}</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={m.id}>
                        <td className="ctr-ord">
                          <button className="ctr-move" aria-label={t('Move up')} disabled={i === 0} onClick={() => void moveMilestone(i, -1)}>↑</button>
                          <button className="ctr-move" aria-label={t('Move down')} disabled={i === milestones.length - 1} onClick={() => void moveMilestone(i, 1)}>↓</button>
                        </td>
                        <td><Tx type="milestone" id={m.id} field="name" original={m.name} as="span" /></td>
                        <td className="voice-figure">{m.due_date ? fmtDate(m.due_date) : <span className="n-none">—</span>}</td>
                        <td className="num voice-figure">{m.amount ? money(m.amount, currency) : <span className="n-none">—</span>}</td>
                        <td><span className={`ctr-tag${m.status === 'accepted' ? ' ctr-tag--ok' : ''}`}>{t(m.status.replace('_', ' '))}</span></td>
                        <td>
                          {m.deliverable_count ? (
                            <span className="ctr-okcell">
                              <span className="voice-figure">{m.deliverable_count}</span>{' '}
                              {m.deliverable_count === 1 ? t('deliverable') : t('deliverables')}
                              {m.acceptance_record_count ? <> · <span className="voice-figure">{m.acceptance_record_count}</span> AR</> : null}
                            </span>
                          ) : m.amount && Number(m.amount) > 0 ? (
                            // Naming what zero MEANS. The old grid printed "0 DEL" — a neutral count.
                            <span className="ctr-warncell"><span className="ctr-lamp ctr-lamp--attend" />{t('money, nothing to accept')}</span>
                          ) : (
                            <span className="n-none">—</span>
                          )}
                        </td>
                        <td>
                          <span className={`ctr-tag${m.published ? ' ctr-tag--strong' : ''}`}>{m.published ? t('visible') : t('hidden')}</span>
                        </td>
                        <td>
                          <div className="ctr-rowverbs">
                            <button
                              className="btn btn-sm"
                              onClick={() => setMsEdit({
                                id: m.id, name: m.name, due_date: m.due_date ?? '',
                                amount: m.amount ?? '', status: m.status, published: m.published,
                              })}
                            >
                              <Pencil className="icon-sm" /> {t('Edit')}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost btn-icon"
                              aria-label={t('Delete milestone {name}', { name: m.name })}
                              title={t('Delete this milestone — refused once it is in the signed record')}
                              onClick={() => void askDelete('milestone', m.id, `"${m.name}"`)}
                            >
                              <Trash2 className="icon-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                  <tr className="ctr-total">
                    <td />
                    <td colSpan={2} className="ctr-muted">{t('Total')}</td>
                    <td className="num voice-figure" style={{ fontWeight: 600 }}>{money(plannedValue, currency)}</td>
                    <td colSpan={4}>
                      {readiness?.estimate_value !== null && readiness?.estimate_value !== undefined
                        && Number(readiness.estimate_value) !== Number(plannedValue) && (
                        <span className="ctr-warncell">
                          <span className="ctr-lamp ctr-lamp--attend" />
                          <span className="src-ref">engagements.estimate_value</span>{' '}
                          {t('says')} {money(readiness.estimate_value, currency)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* The controls of the schedule sheet live INSIDE it. */}
            <div className="ctr-sheetfoot">
              {msAddOpen ? (
                <>
                  <input
                    className="form-input" placeholder={t('New milestone name')} aria-label={t('New milestone name')}
                    value={msDraft.name} onChange={(e) => setMsDraft({ ...msDraft, name: e.target.value })}
                  />
                  <input
                    className="form-input" type="date" aria-label={t('New milestone due date')} style={{ width: 160 }}
                    value={msDraft.due_date} onChange={(e) => setMsDraft({ ...msDraft, due_date: e.target.value })}
                  />
                  <input
                    className="form-input" type="number" step="1000" placeholder={t('amount')} aria-label={t('New milestone amount')} style={{ width: 140 }}
                    value={msDraft.amount} onChange={(e) => setMsDraft({ ...msDraft, amount: e.target.value })}
                  />
                  <button className="btn btn-sm" disabled={!msDraft.name.trim()} onClick={() => void addMilestone()}>
                    <Plus className="icon-sm" /> {t('Add')}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setMsAddOpen(false)}>{t('Cancel')}</button>
                </>
              ) : (
                <button className="btn btn-sm" onClick={() => setMsAddOpen(true)}><Plus className="icon-sm" /> {t('Add a milestone')}</button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ══ ZONE 4 · CHANGE REQUESTS (only once there is one, or a baseline) ══ */}
      {(crs.length > 0 || hasBaseline) && (
        <section className="zone" id="z-cr">
          <div className="zone-head">
            <span className="u-label">{t('Change requests')}</span>
            <span className="zone-note">
              <span className="voice-figure">{openCrCount}</span> {t('open')} ·{' '}
              {hasBaseline
                ? <>{t('the only route through baseline')} <span className="src-ref">{short(baselines[0].content_hash)}</span></>
                : t('no baseline yet — nothing is frozen, so these are still ordinary scope edits')}
            </span>
            <button className="btn btn-sm ctr-zroute" onClick={() => setCrFormOpen(true)}>
              <Plus className="icon-sm" /> {t('New change request')}
            </button>
          </div>

          {crs.length === 0 ? (
            <EmptyState
              kind="empty"
              title={t('No change requests')}
              detail={t('The baseline is frozen and nothing has been asked for since. Every scope move from here raises one.')}
            />
          ) : (
            <div className="card">
              <div className="card-b" style={{ paddingTop: 0 }}>
                {crs.map((cr) => (
                  <button key={cr.id} className="ctr-crrow" onClick={() => void openCr(cr.id)}>
                    <span className="src-ref"><ClaimMark status={cr.status} /> {cr.ref}</span>
                    <span className="ctr-crt">
                      <Tx type="change_request" id={cr.id} field="title" original={cr.title} as="span" />
                      {cr.revision_count > 0 && (
                        <span className="ctr-crm">{t('revised {refs}', { refs: cr.requirement_refs.join(', ') })}</span>
                      )}
                    </span>
                    <span className="ctr-crm">
                      {cr.impact_cost !== null
                        ? <span className="voice-figure">{money(cr.impact_cost, currency)}</span>
                        : cr.goodwill
                          ? <span className="n-none">{t('no charge')}</span>
                          : <span className="ctr-warncell"><span className="ctr-lamp ctr-lamp--attend" />{t('uncosted')}</span>}
                      {cr.impact_days ? <> · <span className="voice-figure">{cr.impact_days}</span> {t('d')}</> : null}
                    </span>
                    <span>
                      <span className={`ctr-tag${cr.status === 'approved' ? ' ctr-tag--strong' : ''}`}>{t(cr.status)}</span>
                      {cr.goodwill && <span className="ctr-tag">{t('goodwill')}</span>}
                      {cr.published && <span className="ctr-tag">{t('visible')}</span>}
                    </span>
                    <ChevronRight className="icon-sm ctr-crm" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ══ THE RECORD — looked up, not checked ═══════════════════════════ */}
      <div className="ctr-drawer" id="z-record">
        <button className="ctr-drawer-h" onClick={() => setRecordOpen((v) => !v)} aria-expanded={recordOpen}>
          <span className="u-label">{tCtx(t, 'The record', 'contract')}</span>
          <span className="ctr-dsub">
            {tp(baselines.length, '{n} baseline', '{n} baselines')} · {tp(allVersions.length, '{n} SOW version', '{n} SOW versions')} ·{' '}
            {tp(clauses.length, '{n} clause', '{n} clauses')} {t('in the library')}
          </span>
          <ChevronRight className="icon-sm ctr-rd-chev" style={recordOpen ? { transform: 'rotate(90deg)' } : undefined} />
        </button>

        {recordOpen && (
          <div className="ctr-drawer-b">
            {/* Baselines */}
            <div className="ctr-grpline">
              <span className="u-label">{t('Baselines')}</span>
              <span className="ctr-cnt">{t('immutable snapshots — the event that freezes the register')}</span>
            </div>
            {baselines.length === 0 ? (
              <p className="ctr-note">
                {t('No baseline yet. One is created the moment a SOW version is signed — that is the event that freezes the register.')}
              </p>
            ) : (
              <div className="ctr-tscroll">
                <table className="tbl ctr-t">
                  <thead>
                    <tr>
                      <th style={{ width: 52 }}>{t('ID')}</th><th>{t('Label')}</th>
                      <th style={{ width: 150 }}>{t('Created')}</th>
                      <th className="num" style={{ width: 110 }}>{t('Requirements')}</th>
                      <th className="num" style={{ width: 110 }}>{t('Deliverables')}</th>
                      <th style={{ width: 150 }}>{t('Hash')}</th>
                      <th style={{ width: 140 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {baselines.map((b) => (
                      <tr key={b.id}>
                        <td className="src-ref">{b.id}</td>
                        <td>{b.label ?? `SOW v${b.version_no}`}{b.is_addendum && <span className="ctr-tag">{t('addendum')}</span>}</td>
                        <td className="voice-figure">{fmtDateTime(b.created_at)}</td>
                        <td className="num voice-figure">{b.requirement_count}</td>
                        <td className="num voice-figure">{b.deliverable_count}</td>
                        <td className="src-ref" title={b.content_hash}>{short(b.content_hash)}</td>
                        <td>
                          <button className="btn btn-sm" onClick={() => { setDiffFrom(String(b.id)); setDiffTo('live'); void runDiff(); }}>
                            {t('Compare to live')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* the diff */}
            {baselines.length > 0 && (
              <>
                <div className="ctr-grpline" style={{ marginTop: 20 }}>
                  <span className="u-label">{t('What has moved')}</span>
                  <span className="ctr-cnt">
                    {diff ? (
                      <>
                        {tp(diff.summary.added + diff.summary.changed + diff.summary.removed, '{n} record', '{n} records')}{' '}
                        {diff.summary.identical ? t('differ') : t('have moved')} {t('between {from} and {to}', { from: diff.from.label, to: diff.to.label })}
                        {crs.filter((c) => c.revision_count > 0).length > 0 && (
                          <>; {crs.filter((c) => c.revision_count > 0)
                            .map((c) => t('{n} under {ref}', { n: c.revision_count, ref: c.ref })).join(', ')}</>
                        )}
                      </>
                    ) : t('pick two points and compare')}
                  </span>
                </div>
                <div className="ctr-diffbar">
                  <label className="form-label" htmlFor="diff-from">{t('From')}</label>
                  <select id="diff-from" className="form-select" style={{ width: 'auto' }} value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)}>
                    {baselines.map((b) => <option key={b.id} value={String(b.id)}>#{b.id} {b.label ?? `SOW v${b.version_no}`}</option>)}
                  </select>
                  <label className="form-label" htmlFor="diff-to">{t('To')}</label>
                  <select id="diff-to" className="form-select" style={{ width: 'auto' }} value={diffTo} onChange={(e) => setDiffTo(e.target.value)}>
                    <option value="live">{t('Live register')}</option>
                    {baselines.map((b) => <option key={b.id} value={String(b.id)}>#{b.id} {b.label ?? `SOW v${b.version_no}`}</option>)}
                  </select>
                  <button className="btn btn-sm" onClick={() => void runDiff()}><GitCompareArrows className="icon-sm" /> {t('Compare')}</button>
                  {engagementId && diffFrom && (
                    <a className="btn btn-sm btn-ghost" target="_blank" rel="noreferrer" href={contractingApi.diffHtmlUrl(engagementId, diffFrom, diffTo)}>
                      <FileDown className="icon-sm" /> {t('Print / export')}
                    </a>
                  )}
                </div>
                {diff && (['requirements', 'deliverables', 'acceptance_criteria', 'milestones'] as const).map((k) => (
                  <DiffCard key={k} title={k.replace(/_/g, ' ')} group={diff[k]} />
                ))}
              </>
            )}

            {/* the signature affirmation — the strongest evidence on the tab */}
            {version?.signatures?.length ? (
              <>
                <div className="ctr-grpline" style={{ marginTop: 20 }}>
                  <span className="u-label">{t('Signature on record')}</span>
                </div>
                {version.signatures.map((s, i) => (
                  <div key={s.id ?? i} className="mb-12">
                    <div className="ev">{s.affirmation}</div>
                    <div className="ev-src">
                      <span>{s.typed_name}</span><span className="sep">·</span>
                      <span className="voice-figure">{fmtDateTime(s.signed_at)}</span><span className="sep">·</span>
                      <span className="src-ref">{s.ip_address ?? t('no ip recorded')}</span><span className="sep">·</span>
                      <span className="src-ref">{s.user_agent ?? t('no agent recorded')}</span><span className="sep">·</span>
                      <span className="src-ref" title={s.content_hash}>{short(s.content_hash)}</span>
                    </div>
                  </div>
                ))}
              </>
            ) : null}

            {/* the clause library */}
            <div className="ctr-grpline" style={{ marginTop: 20 }}>
              <span className="u-label">{t('Clause library')}</span>
              <span className="ctr-cnt">{t('shared across every engagement — an edit creates the NEXT version')}</span>
            </div>
            <div className="ctr-clauselib">
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="card-title">{t('Library')}</h3>
                  <button
                    className="btn btn-sm"
                    onClick={() => { setClauseNew(true); setClauseKey(null); setClauseUsage(null); setClauseDraft({ key: '', title: '', body_md: '' }); }}
                  >
                    <Plus className="icon-sm" /> {t('New')}
                  </button>
                </div>
                <div className="p-8">
                  {clauses.map((c) => (
                    <button
                      key={c.key}
                      className={`selectable${clauseKey === c.key ? ' active' : ''}`}
                      style={{ width: '100%', textAlign: 'left' }}
                      onClick={() => void selectClause(c.key)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{c.title}</span>
                        <span className="ctr-tag voice-figure">v{c.latest.version}</span>
                      </div>
                      <div className="text-xs text-ink-3"><span className="src-ref">{c.key}</span>{!c.active && ` · ${t('retired')}`}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="card-title">{clauseNew ? t('New clause') : clauseKey ? t('Revise {key}', { key: clauseKey }) : t('Select a clause')}</h3>
                  {clauseKey && !clauseNew && (
                    <button className="btn btn-sm btn-ghost" onClick={() => void toggleClauseActive()}>
                      {activeClause?.active ? t('Retire') : t('Restore')}
                    </button>
                  )}
                </div>
                {(clauseNew || clauseKey) ? (
                  <div className="p-12">
                    <div className="ctr-note mb-12">
                      <ShieldCheck className="icon-sm icon-inline" /> {t('Saving creates the NEXT version. Signed SOWs keep the version they printed — nothing already agreed changes retroactively.')}
                    </div>
                    {clauseNew && (
                      <div className="form-group">
                        <label className="form-label" htmlFor="clause-key">{t('Key')}</label>
                        <input
                          id="clause-key" className="form-input" placeholder={t('e.g. subcontracting')}
                          value={clauseDraft.key} onChange={(e) => setClauseDraft({ ...clauseDraft, key: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label" htmlFor="clause-title">{t('Title')}</label>
                      <input
                        id="clause-title" className="form-input"
                        value={clauseDraft.title} onChange={(e) => setClauseDraft({ ...clauseDraft, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="clause-body">{t('Body — markdown: paragraphs, **bold**, - bullets')}</label>
                      <textarea
                        id="clause-body" className="form-textarea" rows={14}
                        value={clauseDraft.body_md} onChange={(e) => setClauseDraft({ ...clauseDraft, body_md: e.target.value })}
                      />
                    </div>
                    <button
                      className="btn"
                      disabled={busy || clauseDraft.body_md.trim().length < 20 || !clauseDraft.title.trim() || (clauseNew && !clauseDraft.key.trim())}
                      onClick={() => void saveClause()}
                    >
                      <Save className="icon-sm" /> {clauseNew ? t('Create clause') : t('Save as new version')}
                    </button>

                    {clauseUsage && (
                      <div className="mt-24">
                        <div className="u-label mb-8">{t('Usage')}</div>
                        <div className="text-sm mb-8">
                          <span className="voice-figure">{clauseUsage.total}</span> {t('SOW version(s) cite this clause')} ·{' '}
                          <span className={`voice-figure${clauseUsage.stale ? ' text-warn' : ''}`}>{clauseUsage.stale}</span> {t('on a superseded version')}
                          {clauseUsage.signed_on_old_version > 0 && (
                            <> · <span className="voice-figure text-warn">{clauseUsage.signed_on_old_version}</span> {t('of those are SIGNED and keep the old wording')}</>
                          )}
                        </div>
                        {clauseUsage.rows.length > 0 && (
                          <div className="ctr-tscroll">
                            <table className="tbl ctr-t">
                              <thead>
                                <tr>
                                  <th>{t('Engagement')}</th><th style={{ width: 80 }}>{t('Version')}</th>
                                  <th style={{ width: 110 }}>{t('Status')}</th><th style={{ width: 110 }}>{t('Clause')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {clauseUsage.rows.map((r) => (
                                  <tr key={r.sow_version_id}>
                                    <td><span className="src-ref">{r.engagement_code}</span> {r.engagement_name}</td>
                                    <td className="src-ref">v{r.version_no}</td>
                                    <td><StatusBadge status={r.status} /></td>
                                    <td className="src-ref">
                                      v{r.clause_version}{r.stale && <span className="ctr-tag ctr-tag--attend">{t('stale')}</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-12 ctr-note">
                    {t('Pick a clause to read it, revise it or see which SOW versions cite it.')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════ CR PANEL ══════════════════════════════ */}
      {crDetail && (
        <div className="panel-overlay open" onClick={() => setCrDetail(null)}>
          <div className="panel-overlay-bg" />
          <div className="panel-slide open" onClick={(e) => e.stopPropagation()}>
            <div className="panel-slide-head">
              <h2 className="panel-slide-title">
                <span className="src-ref">{crDetail.ref}</span>{' '}
                <Tx type="change_request" id={crDetail.id} field="title" original={crDetail.title} as="span" />
              </h2>
              <button className="panel-slide-close" onClick={() => setCrDetail(null)}><X className="icon-sm" /></button>
            </div>
            <div className="panel-slide-body">
              <TxTools type="change_request" id={crDetail.id} field="title" original={crDetail.title} />
              <div className="flex gap-8 items-center mb-12">
                <StatusBadge status={crDetail.status} />
                {crDetail.goodwill && <span className="badge badge-ok">{t('goodwill')}</span>}
                {crDetail.published && <span className="badge badge-accent">{t('visible to client')}</span>}
              </div>

              <div className="panel-section">
                <div className="u-label mb-8">{t('What was asked for')}</div>
                {crDetail.description ? (
                  <>
                    <div className="ev">{crDetail.description}</div>
                    <div className="ev-src">
                      <span>{crDetail.origin || t('origin not recorded')}</span><span className="sep">·</span>
                      {t('raised')} <span className="voice-figure">{fmtDateTime(crDetail.created_at)}</span>
                      {crDetail.origin_document_ref && (
                        <><span className="sep">·</span><span className="src-ref">{crDetail.origin_document_ref}</span></>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="n-none text-sm">{t('Nothing was written down. A change request with no description is a title and a price.')}</p>
                )}
              </div>

              <div className="panel-section">
                <div className="flex items-center justify-between mb-8">
                  <div className="u-label">{t('Impact')}</div>
                  {crEditable && !impactEdit && (
                    <button className="btn btn-sm" onClick={() => startImpactEdit(crDetail)}>
                      <Calculator className="icon-sm" /> {t('Edit impact')}
                    </button>
                  )}
                </div>

                {impactEdit ? (
                  <>
                    <div className="flex gap-8">
                      <div className="form-group flex-1">
                        <label className="form-label" htmlFor="impact-cost">{t('Cost ({currency})', { currency })}</label>
                        <input
                          id="impact-cost" className="form-input" type="number" step="100" autoFocus
                          value={impactEdit.impact_cost}
                          onChange={(e) => setImpactEdit({ ...impactEdit, impact_cost: e.target.value })}
                        />
                      </div>
                      <div className="form-group flex-1">
                        <label className="form-label" htmlFor="impact-days">{t('Schedule (days)')}</label>
                        <input
                          id="impact-days" className="form-input" type="number" step="0.5"
                          value={impactEdit.impact_days}
                          onChange={(e) => setImpactEdit({ ...impactEdit, impact_days: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="impact-risk">{t('Risk and dependencies')}</label>
                      <textarea
                        id="impact-risk" className="form-textarea" rows={3}
                        placeholder={t('What this change puts at risk, and what it depends on.')}
                        value={impactEdit.impact_risk}
                        onChange={(e) => setImpactEdit({ ...impactEdit, impact_risk: e.target.value })}
                      />
                    </div>
                    <label className="flex items-center gap-8 text-sm mb-8">
                      <input
                        type="checkbox" checked={impactEdit.goodwill}
                        onChange={(e) => setImpactEdit({ ...impactEdit, goodwill: e.target.checked })}
                      />
                      <span>{t('Goodwill — absorbed at no charge, still on the record')}</span>
                    </label>
                    <div className="flex gap-8">
                      <button className="btn" disabled={busy} onClick={() => void saveImpact()}>
                        <Save className="icon-sm" /> {t('Save impact')}
                      </button>
                      <button className="btn btn-ghost" onClick={() => setImpactEdit(null)}>{t('Cancel')}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm">
                      {t('Cost')}{' '}
                      {crDetail.impact_cost !== null
                        ? <span className="voice-figure">{money(crDetail.impact_cost, currency)}</span>
                        : crDetail.goodwill ? <span className="n-none">{t('no charge')}</span> : <span className="n-none">{t('not costed')}</span>}
                      {' · '}{t('Schedule')}{' '}
                      {crDetail.impact_days !== null
                        ? <><span className="voice-figure">{crDetail.impact_days}</span> {t('day(s)')}</>
                        : <span className="n-none">{t('no schedule impact recorded')}</span>}
                    </div>
                    {crDetail.impact_risk && <p className="text-sm text-ink-2 mt-4">{crDetail.impact_risk}</p>}
                    {/* A goodwill CR is DELIBERATELY uncosted — 'no charge' is the number. */}
                    {crUncosted && (
                      <div className="text-sm text-warn mt-8">
                        <AlertTriangle className="icon-sm icon-inline" /> {t('Uncosted.')} {crEditable
                          ? t('Submitting publishes it to the portal, where the sponsor is asked to approve a number nobody has written down.')
                          : t('It was decided without one.')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {crDetail.decided_at && (
                <div className="panel-section">
                  <div className="u-label mb-8">{t('Decision')}</div>
                  <div className="text-sm">
                    {t(crDetail.status)} {t('on')} <span className="voice-figure">{fmtDateTime(crDetail.decided_at)}</span>
                    {crDetail.decided_by_portal_name && <> {t('by {name} (client)', { name: crDetail.decided_by_portal_name })}</>}
                    {crDetail.decided_by_name && <> · {t('recorded by {name}', { name: crDetail.decided_by_name })}</>}
                    . {t('A decided change request is part of the record; its impact is frozen with the decision.')}
                  </div>
                </div>
              )}

              {crDetail.revisions.length > 0 && (
                <div className="panel-section">
                  <div className="u-label mb-8">{t('Register revisions this produced')}</div>
                  {crDetail.revisions.map((r) => (
                    <div key={r.id} className="mb-12">
                      <div className="ctr-itemhead">
                        <span className="src-ref">{r.requirement_ref}</span> {t('rev')}{' '}
                        <span className="voice-figure">{r.revision_no}</span> ·{' '}
                        <span className="ctr-tag">{t(r.requirement_status)}</span>
                      </div>
                      {r.previous_statement && (
                        <div className="ev ctr-ev-was">{r.previous_statement}</div>
                      )}
                      <div className="ev" style={{ marginTop: 6 }}>{r.statement}</div>
                      <div className="ev-src">
                        <span className="src-ref">requirement_revisions</span><span className="sep">·</span>
                        {t('carries')} <span className="src-ref">{crDetail.ref}</span>{t(', which is what let it through the baseline lock')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {crDetail.comments.length > 0 && (
                <div className="panel-section">
                  <div className="u-label mb-8">{t('Notes')}</div>
                  {crDetail.comments.map((c) => (
                    <div key={c.id} className="mb-12">
                      <div className="ev">{c.body}</div>
                      <div className="ev-src">
                        <span>{c.portal_user_name || c.operator_name || t('system')}</span>
                        <span className="sep">·</span>
                        <span className="voice-figure">{fmtDateTime(c.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {crEditable ? (
                <div className="panel-section flex gap-8 flex-wrap">
                  {crDetail.status === 'draft' && (
                    <button className="btn" onClick={() => void submitCr(crDetail)}>
                      <Send className="icon-sm" /> {t('Submit to client')}
                    </button>
                  )}
                  <button className="btn" onClick={() => void openApprove(crDetail)}>
                    <Check className="icon-sm" /> {t('Approve and apply')}
                  </button>
                  <button className="btn" onClick={() => void setCrStatus(crDetail, 'deferred')}>{t('Defer')}</button>
                  <button className="btn" onClick={() => void setCrStatus(crDetail, 'rejected')}>
                    <X className="icon-sm" /> {t('Reject')}
                  </button>
                  <button className="btn btn-ghost" onClick={() => void setCrStatus(crDetail, 'withdrawn')}>{t('Withdraw')}</button>
                  {/*
                    Reject/withdraw keep a change request that was really asked for. Delete is for
                    the one that never should have been raised — a duplicate, a typo, the wrong
                    engagement. The server refuses it once revisions exist.
                  */}
                  <button
                    className="btn btn-ghost ml-auto"
                    title={t('Delete this change request — refused once it has revised the register')}
                    onClick={() => void askDelete('change_request', crDetail.id, crDetail.ref)}
                  >
                    <Trash2 className="icon-sm" /> {t('Delete')}
                  </button>
                </div>
              ) : (
                <div className="panel-section flex gap-8 flex-wrap">
                  {crDetail.status === 'approved' && (
                    <button className="btn" onClick={() => void openApprove(crDetail)}>
                      <Check className="icon-sm" /> {t('Apply further revisions')}
                    </button>
                  )}
                  <button
                    className="btn btn-ghost ml-auto"
                    title={t('Delete this change request — refused once it has revised the register')}
                    onClick={() => void askDelete('change_request', crDetail.id, crDetail.ref)}
                  >
                    <Trash2 className="icon-sm" /> {t('Delete')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════ MODALS ════════════════════════════════ */}

      {/*
        Issuing re-renders the document without the draft marker and re-reads the register, so
        the draft hash is NOT the hash the client signs. Quote the real one, show whether the
        register moved, and make unverifiable acceptance criteria a conscious decision.
      */}
      {issueTarget && (
        <div className="modal-overlay" onClick={() => setIssueTarget(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Issue version {n}', { n: issueTarget.version_no })}</h2>
              <button className="close-btn" onClick={() => setIssueTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              {!issueCheck ? (
                <div className="flex items-center gap-8 text-sm text-ink-3"><span className="spinner spinner-sm" /> {t('Checking what issuing would do…')}</div>
              ) : (
                <>
                  <table className="tbl ctr-t mb-16">
                    <tbody>
                      <tr>
                        <td className="text-ink-3" style={{ width: 190 }}>{t('Hash the client signs')}</td>
                        <td className="src-ref" style={{ overflowWrap: 'anywhere' }}>{issueCheck.issued_hash}</td>
                      </tr>
                      <tr>
                        <td className="text-ink-3">{t('Draft preview hash')}</td>
                        <td className="src-ref text-ink-3" style={{ overflowWrap: 'anywhere' }}>
                          {issueCheck.draft_hash ?? '—'}
                          {issueCheck.hash_changes && (
                            <div className="ctr-note mt-4">
                              {t('Different by design: the draft carries the “DRAFT — not for signature” marker and the operator-only lint notes; the issued document carries neither.')}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {issueCheck.register_snapshot_changed && (
                    <div className="card p-12 mb-16" style={{ borderLeft: '2px solid var(--warn)' }}>
                      <div className="flex items-start gap-8">
                        <AlertTriangle className="icon-sm" />
                        <div className="text-sm">
                          <strong>{t('The register has moved since this draft was rendered.')}</strong>{' '}
                          {t("Issuing re-reads it, so the client gets today's register — not what the preview shows. Re-save the draft first if you want to see exactly what goes out.")}
                        </div>
                      </div>
                    </div>
                  )}

                  {issueCheck.lint_failure_count > 0 && (
                    <div className="card p-12 mb-16" style={{ borderLeft: '2px solid var(--err)' }}>
                      <div className="flex items-start gap-8 mb-8">
                        <AlertTriangle className="icon-sm" />
                        <div className="text-sm">
                          <strong>{t('{n} acceptance criterion/criteria in Annex A are not verifiable as written.', { n: issueCheck.lint_failure_count })}</strong>{' '}
                          {t('Once issued they are contractual, and nobody — not the client, not you — can prove they are met. These are the same rows the pre-flight above lists.')}
                        </div>
                      </div>
                      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {issueCheck.lint_failures.map((f) => (
                          <div className="ctr-item" key={f.ac_ref}>
                            <div className="ctr-itemhead">
                              <span className="src-ref">{f.ac_ref}</span> <span className="sep">·</span>{' '}
                              <span className="src-ref">{f.requirement_ref}</span>
                            </div>
                            <div className="ev">{f.statement}</div>
                            <div className="ctr-lint ctr-lint--err">{t('Linter:')} {f.errors.join(' ') || t('Fails the acceptance-criterion linter.')}</div>
                          </div>
                        ))}
                      </div>
                      <label className="flex items-start gap-8 text-sm mt-12">
                        <input type="checkbox" checked={acceptUnverifiable} onChange={(e) => setAcceptUnverifiable(e.target.checked)} />
                        <span>
                          {t('I understand these acceptance criteria will be')} <strong>{t('contractual as written')}</strong>{t(', and I am issuing them anyway. (Otherwise: fix them on the Register page, re-save the draft, and come back.)')}
                        </span>
                      </label>
                    </div>
                  )}

                  <p className="text-sm text-ink-2">
                    {t('The document is published to the client portal for signature and a PDF is generated. Further edits need a new version.')}
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setIssueTarget(null)}>{t('Cancel')}</button>
              <button
                className="btn btn-accent"
                disabled={busy || !issueCheck || (issueCheck.lint_failure_count > 0 && !acceptUnverifiable)}
                onClick={() => void doIssue()}
              >
                <Send className="icon-sm" /> {t('Issue and publish')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!submitTarget}
        title={t('Submit {ref} uncosted?', { ref: submitTarget?.ref ?? '' })}
        danger
        confirmLabel={t('Submit uncosted')}
        onCancel={() => setSubmitTarget(null)}
        onConfirm={() => { if (submitTarget) void submitCr(submitTarget, true); }}
      >
        <p className="text-sm text-ink-2">
          <strong>{submitTarget?.ref}</strong> {t('has neither a cost nor a schedule impact. Submitting publishes it to the client portal, where the sponsor is asked to approve it — effectively agreeing to an unknown number.')}
        </p>
        <p className="text-sm text-ink-2 mt-8">
          {t('Close this, use')} <strong>{t('Edit impact')}</strong> {t('to cost it, then submit. Continue only if the impact is genuinely nil and you want that on the record.')}
        </p>
      </ConfirmModal>

      {/*
        One delete dialog for milestones, change requests and draft SOW versions. The plan comes
        from the server before the dialog opens: if the delete is allowed the cascade is named in
        plain words, and if it is refused the operator gets the reason and the alternative — never
        a dead button.
      */}
      {del && del.plan.allowed && (
        <ConfirmModal
          open
          title={t('Delete {name}?', { name: del.name })}
          danger
          confirmLabel={delBusy ? t('Deleting…') : t('Delete permanently')}
          onCancel={() => setDel(null)}
          onConfirm={() => void doDelete()}
        >
          <p className="text-sm text-ink-3 mb-8">{del.plan.label}</p>
          <CascadeList plan={del.plan} />
          <p className="text-xs text-ink-3 mt-12">
            {t('Immediate and permanent — this is for something that should never have existed. If it was real and simply ended, retire it instead ({alternative}). The audit log keeps the deleted row.', {
              alternative: del.kind === 'milestone'
                ? t('set the status to delivered or accepted')
                : del.kind === 'change_request' ? t('reject or withdraw it') : t('issue it and supersede it later'),
            })}
          </p>
        </ConfirmModal>
      )}

      {del && !del.plan.allowed && (
        <BlockedDeleteModal title={t('{name} cannot be deleted', { name: del.name })} plan={del.plan} onClose={() => setDel(null)} />
      )}

      {newSowOpen && (
        <div className="modal-overlay" onClick={() => setNewSowOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('New Statement of Work')}</h2>
              <button className="close-btn" onClick={() => setNewSowOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="sow-title">{t('Title')}</label>
                <input
                  id="sow-title" className="form-input" placeholder={t('Statement of Work — {name}', { name: current.name })}
                  value={newSowTitle} onChange={(e) => setNewSowTitle(e.target.value)}
                />
              </div>
              <p className="ctr-note">
                {t('Version 1 is rendered from the live register immediately.')}
                {Object.keys(staged).length > 0 && (
                  <> {t('The {values} you accepted from the record ({keys}) go in with it.', {
                    values: tp(Object.keys(staged).length, '{n} value', '{n} values'),
                    keys: Object.keys(staged).join(', '),
                  })}</>
                )}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setNewSowOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" onClick={() => void createSow()}>{t('Create and render')}</button>
            </div>
          </div>
        </div>
      )}

      {signOpen && version && (
        <div className="modal-overlay" onClick={() => setSignOpen(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Record the signature on v{n}', { n: version.version_no })}</h2>
              <button className="close-btn" onClick={() => setSignOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="card p-12 mb-16" style={{ borderLeft: '2px solid var(--warn)' }}>
                <div className="flex items-start gap-8">
                  <AlertTriangle className="icon-sm" />
                  <div className="text-sm">
                    <strong>{t('This freezes the baseline.')}</strong>{' '}
                    {t('Recording the signature serialises the whole register as an immutable snapshot, hashes it, and flips every held requirement to')}{' '}
                    <span className="src-ref">baselined</span>. {t('After this, scope moves only through a change request.')}
                  </div>
                </div>
              </div>
              <div className="ctr-note mb-12">{t('Document hash')} <span className="src-ref">{version.content_hash}</span></div>
              <div className="form-group">
                <label className="form-label" htmlFor="sign-name">{t('Client signatory — typed name, exactly as signed')}</label>
                <input id="sign-name" className="form-input" value={signName} onChange={(e) => setSignName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="sign-date">{t('Date signed (optional — defaults to now)')}</label>
                <input id="sign-date" className="form-input" type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} />
              </div>
              <label className="flex items-start gap-8 text-sm">
                <input type="checkbox" checked={signAffirmed} onChange={(e) => setSignAffirmed(e.target.checked)} />
                <span>
                  {t('I confirm the named signatory has signed this exact document, and I understand that recording it freezes the requirements register as an immutable baseline.')}
                </span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setSignOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={!signName.trim() || !signAffirmed || busy} onClick={() => void doSign()}>
                <PenLine className="icon-sm" /> {t('Record signature and baseline')}
              </button>
            </div>
          </div>
        </div>
      )}

      {crFormOpen && (
        <div className="modal-overlay" onClick={() => setCrFormOpen(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('New change request')}</h2>
              <button className="close-btn" onClick={() => setCrFormOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="cr-title">{t('Title')}</label>
                <input id="cr-title" className="form-input" value={String(crForm.title)} onChange={(e) => setCrForm({ ...crForm, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cr-desc">{t('What is being asked for, and why')}</label>
                <textarea id="cr-desc" className="form-textarea" rows={5} value={String(crForm.description)} onChange={(e) => setCrForm({ ...crForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cr-origin">{t('Origin — who asked, when')}</label>
                <input id="cr-origin" className="form-input" value={String(crForm.origin)} onChange={(e) => setCrForm({ ...crForm, origin: e.target.value })} />
              </div>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label className="form-label" htmlFor="cr-cost">{t('Impact — cost')}</label>
                  <input id="cr-cost" className="form-input" type="number" value={String(crForm.impact_cost)} onChange={(e) => setCrForm({ ...crForm, impact_cost: e.target.value })} />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label" htmlFor="cr-days">{t('Impact — days')}</label>
                  <input id="cr-days" className="form-input" type="number" value={String(crForm.impact_days)} onChange={(e) => setCrForm({ ...crForm, impact_days: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cr-risk">{t('Impact — risk')}</label>
                <textarea id="cr-risk" className="form-textarea" rows={2} value={String(crForm.impact_risk)} onChange={(e) => setCrForm({ ...crForm, impact_risk: e.target.value })} />
              </div>
              <label className="flex items-center gap-8 text-sm">
                <input type="checkbox" checked={!!crForm.goodwill} onChange={(e) => setCrForm({ ...crForm, goodwill: e.target.checked })} />
                <span>{t('Goodwill — absorbed at no charge, but recorded so the concession stays visible')}</span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setCrFormOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={!String(crForm.title).trim()} onClick={() => void submitCrForm()}>{t('Create draft')}</button>
            </div>
          </div>
        </div>
      )}

      {approveOpen && crDetail && (
        <div className="modal-overlay" onClick={() => setApproveOpen(false)}>
          <div className="modal modal-full" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Approve {ref} — compose the register revisions', { ref: crDetail.ref })}</h2>
              <button className="close-btn" onClick={() => setApproveOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="read mb-12">
                {t('Approval and the revisions are applied in one transaction. Every revision carries')}{' '}
                <span className="src-ref">{crDetail.ref}</span>{t(', which is what lets it through the baseline lock and what answers “why did this change?” later. Pick the requirement and its current wording appears — you are editing text you can read, not a truncated line in a dropdown.')}
              </p>

              {instructions.map((ins, i) => {
                const picked = registerRows.find((r) => r.id === ins.requirement_id) ?? null;
                return (
                  <div className="card p-12 mb-8" key={i}>
                    <div className="flex gap-8 items-center mb-8">
                      <select
                        className="form-select" style={{ width: 130 }} value={ins.action} aria-label={t('Revision action')}
                        onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, action: e.target.value as RevisionInstruction['action'] } : x)))}
                      >
                        <option value="modify">{t('modify')}</option>
                        <option value="create">{t('create')}</option>
                        <option value="descope">{t('descope')}</option>
                      </select>
                      {ins.action !== 'create' && (
                        <select
                          className="form-select flex-1" value={ins.requirement_id ?? ''} aria-label={t('Requirement')}
                          onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, requirement_id: Number(e.target.value) } : x)))}
                        >
                          <option value="">{t('Pick a requirement…')}</option>
                          {registerRows.map((r) => (
                            <option key={r.id} value={r.id}>{r.ref} — {(r.statement ?? '').slice(0, 90)}</option>
                          ))}
                        </select>
                      )}
                      <button className="btn btn-icon btn-ghost" aria-label={t('Remove revision')} onClick={() => setInstructions(instructions.filter((_, j) => j !== i))}>
                        <Trash2 className="icon-sm" />
                      </button>
                    </div>

                    {/*
                      THE CURRENT WORDING, printed. The data was already in this modal's state and
                      was used only to fill an <option> truncated at 90 characters — the operator
                      was asked to rewrite a requirement they could not see.
                    */}
                    {picked && (
                      <div className="mb-8">
                        <div className="ctr-itemhead">{t('Current wording — this is what changes')}</div>
                        <div className="ev">{picked.statement}</div>
                        <div className="ev-src">
                          <span className="src-ref">requirement_revisions.statement</span>
                          <span className="sep">·</span>{t('the register speaking')}
                        </div>
                      </div>
                    )}

                    {ins.action === 'descope' ? (
                      <input
                        className="form-input" placeholder={t('Reason for removing it from scope')} aria-label={t('Descope reason')}
                        value={ins.reason ?? ''}
                        onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))}
                      />
                    ) : (
                      <>
                        <textarea
                          className="form-textarea" rows={3} aria-label={t('Statement')}
                          placeholder={ins.action === 'create' ? t('New requirement statement') : t('Edited statement (leave empty to keep the current wording)')}
                          value={ins.statement ?? ''}
                          onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, statement: e.target.value } : x)))}
                        />
                        <input
                          className="form-input mt-4" placeholder={t('Rationale')} aria-label={t('Rationale')}
                          value={ins.rationale ?? ''}
                          onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, rationale: e.target.value } : x)))}
                        />
                        {ins.action === 'create' && (
                          <div className="flex gap-8 mt-4">
                            <select
                              className="form-select" value={ins.type ?? 'functional'} aria-label={t('Type')}
                              onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}
                            >
                              {['functional', 'non_functional', 'constraint', 'assumption', 'out_of_scope'].map((ty) => <option key={ty} value={ty}>{t(ty.replace(/_/g, ' '))}</option>)}
                            </select>
                            <select
                              className="form-select" value={ins.priority ?? 'should'} aria-label={t('Priority')}
                              onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, priority: e.target.value } : x)))}
                            >
                              {['must', 'should', 'could', 'wont'].map((p) => <option key={p} value={p}>{t(p)}</option>)}
                            </select>
                            <input
                              className="form-input" placeholder={t('Category')} aria-label={t('Category')}
                              value={ins.category ?? ''}
                              onChange={(e) => setInstructions(instructions.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              <button className="btn btn-sm" onClick={() => setInstructions([...instructions, { action: 'modify' }])}>
                <Plus className="icon-sm" /> {t('Add a revision')}
              </button>

              <div className="mt-16 flex flex-col gap-8">
                <label className="flex items-center gap-8 text-sm">
                  <input type="checkbox" checked={approveGoodwill} onChange={(e) => setApproveGoodwill(e.target.checked)} />
                  <span>{t('Record as goodwill — absorbed at no charge, still on the record')}</span>
                </label>
                <label className="flex items-center gap-8 text-sm">
                  <input type="checkbox" checked={approveAddendum} onChange={(e) => setApproveAddendum(e.target.checked)} />
                  <span>{t('Draft an addendum SOW version from the register after applying')}</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setApproveOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={busy} onClick={() => void doApprove()}>
                <Check className="icon-sm" /> {t('Approve and apply {n} revision(s)', { n: instructions.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One diff group — added / changed / removed, with the field deltas spelled out. */
function DiffCard({ title, group }: { title: string; group: DiffGroup }) {
  const t = useT();
  const total = group.added.length + group.changed.length + group.removed.length;
  if (total === 0) {
    return (
      <div className="card mb-16">
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title text-upper">{t(title)}</h3>
          <span className="ctr-note"><span className="voice-figure">{group.unchanged}</span> {t('identical, no changes')}</span>
        </div>
      </div>
    );
  }
  const rows = [
    ...group.added.map((entry) => ({ kind: 'added', cls: 'badge-ok', entry })),
    ...group.changed.map((entry) => ({ kind: 'changed', cls: 'badge-warn', entry })),
    ...group.removed.map((entry) => ({ kind: 'removed', cls: 'badge-err', entry })),
  ];
  return (
    <div className="card mb-16">
      <div className="card-header flex items-center justify-between">
        <h3 className="card-title text-upper">{t(title)}</h3>
        <span className="ctr-note voice-figure">
          <span className="text-ok">+{group.added.length}</span>{' '}
          <span className="text-warn">~{group.changed.length}</span>{' '}
          <span className="text-err">−{group.removed.length}</span>{' '}
          {group.unchanged} {t('identical')}
        </span>
      </div>
      <div className="ctr-tscroll">
        <table className="tbl ctr-t">
          <thead><tr><th style={{ width: 100 }}>{t('Ref')}</th><th style={{ width: 90 }}>{t('Change')}</th><th>{t('Detail')}</th></tr></thead>
          <tbody>
            {rows.map(({ kind, cls, entry }) => (
              <tr key={`${kind}-${entry.ref}`}>
                <td className="src-ref">{entry.ref}</td>
                <td><span className={`badge ${cls}`}>{t(kind)}</span></td>
                <td>
                  {entry.label}
                  {entry.changes.map((c) => (
                    <div key={c.field} className="text-xs mt-4">
                      <span className="u-label">{c.field.replace(/_/g, ' ')}</span>{' '}
                      <span className="text-err" style={{ textDecoration: 'line-through' }}>{String(c.before ?? '—')}</span>{' '}
                      <span className="text-ok">{String(c.after ?? '—')}</span>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
